import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import type { 
  AttributionFactors, 
  ImplementationInvestment, 
  TimeToValueInputs, 
  CalculatorFormData,
  CalculatorResults
} from './types';
import { 
  INDUSTRY_DEFAULTS,
  convertFTEtoAnnualHires,
  type IndustryName 
} from '../../data/industryDefaults';
import { IndustryDropdown } from '../../components/IndustryDropdown';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import roosterIcon from '../../assets/images/rooster.ico';
import { useVersion } from '../../contexts/VersionContext';
import {
  saveUserCalculatorSession,
  isFirebaseConfigured,
  generateSessionId,
  initializeAnalyticsSession,
  updateAnalyticsSession,
  saveCheckpoint,
  markSessionCompleted,
  trackCalculatorReturn,
  trackTimeSpent
} from './services/firestoreService';
import { debounce } from '../../utils/debounce';
import { generatePDFReport } from './ReportGenerator';
import { generateTasPDFReport } from './TasReportGenerator';
import { EmailDialog } from './components/EmailDialog';
import { saveFinalizedReport, storeReportMetadata, getReportById } from '../../services/reportStorage';
import { createUserDocumentId } from '../../utils/crypto';
// Import new clean UI components
import { SalaryVacancyGroup } from './components/SalaryVacancyGroup';
import { HiringProfileCalculator } from './components/HiringProfileCalculator';
import { FieldHelpModal } from './components/FieldHelpModal';
import { FIELD_HELP_CONTENT } from './data/helpContent';

// Feature improvement components
import { FeatureRecommendations } from './components/FeatureRecommendations';
import { getTopFeaturesForComponent, getRecommendedFeatures, calculateFeatureTotalImpact } from '../../data/roosterFeatures';

// Time-to-Value components and services
import { useTimeToValue } from './hooks/useTimeToValue';
// ScenarioPresets component is available but not currently used in the simplified interface
import { TimelineSettings } from './components/TimelineSettings';
import { RealizationFactors } from './components/RealizationFactors';
import { InvestmentMode } from './components/InvestmentMode';
import { ROIRampChart } from '../../components/charts/ROIRampChart';
import {
  calculateYear1Savings,
  calculateSteadyStateSavings,
  calculateImplementationInvestment,
  generateMonthlyProjections,
  calculatePaybackPeriod,
} from './services/timeToValueService';


interface FormData {
  // Contact Information
  organizationName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactTitle: string;
  
  // Organization Details
  industry: string;
  annualHires: number | string;
  schedulingMethods: string[];  // Changed to array for multi-select
  avgConfirmationTime: number | string;
  adminTimePerHire: number | string;
  candidateSatisfaction: number | string;
  interviewerSatisfaction: number | string;
  reschedulingRate: number | string;
  interviewerAvailability: number | string;
  interviewNoShowRate: number | string;
  timeToFill: number | string;
  costPerHire: number | string;
  dailyVacancyCost: number | string;
  avgSalary: number | string;
  tasImprovement: number | string;
  currency?: 'USD' | 'EUR';
  calculatorPath?: string;  // The URL path used to access the calculator
}

enum CalculatorStep {
  OrganizationDetails = 1,
  CurrentMetrics = 2,
  TasScore = 3,
  ROIAnalysis = 4
}


// Tooltip Component
const InfoTooltip = ({ 
  title, 
  description, 
  calculationFormula, 
  additionalInfo,
  improvementNote,
  topFeatures
}: { 
  title: string; 
  description: string; 
  calculationFormula?: string;
  additionalInfo?: string | React.ReactNode;
  improvementNote?: string;
  topFeatures?: Array<{ name: string; impact: number; icon?: string }>;
}) => {
  const handleMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    const tooltip = e.currentTarget.querySelector('.tooltip');
    if (tooltip && tooltip instanceof HTMLElement) {
      const rect = e.currentTarget.getBoundingClientRect();
      const tooltipWidth = 270;
      
      // Calculate position ensuring tooltip doesn't go off screen
      // Center tooltip by default, but adjust if too close to screen edges
      let leftPos = rect.left - (tooltipWidth / 2) + 10;
      
      // Adjust if tooltip would go off screen
      if (leftPos < 20) leftPos = 20;
      if (leftPos + tooltipWidth > window.innerWidth - 20) {
        leftPos = window.innerWidth - tooltipWidth - 20;
      }
      
      // Calculate vertical position - ensure it's not cut off at the top
      let topPos = rect.top - tooltip.offsetHeight - 10;
      if (topPos < 10) {
        // If too close to top, position below instead
        topPos = rect.bottom + 10;
      }
      
      tooltip.style.left = leftPos + 'px';
      tooltip.style.top = topPos + 'px';
    }
  };

  return (
    <div className="ml-1 text-sky-500 cursor-help">
      <div className="tooltip-container" onMouseOver={handleMouseOver}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4 hover:text-sky-700">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="tooltip">
          <p className="font-bold text-gray-800 mb-1">{title}</p>
          <p className="mb-1">{description}</p>
          {improvementNote && (
            <>
              <p className="font-bold text-gray-700">Why it improves:</p>
              <p>{improvementNote}</p>
            </>
          )}
          {calculationFormula && (
            <>
              <p className="font-bold text-gray-700">Calculation:</p>
              <p className="text-xs mb-1">{calculationFormula}</p>
            </>
          )}
          {additionalInfo && (
            typeof additionalInfo === 'string' ? (
              <p className="text-xs italic mt-2">{additionalInfo}</p>
            ) : (
              additionalInfo
            )
          )}
          {topFeatures && topFeatures.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-bold text-gray-700 mb-1">Top Rooster Features:</p>
              <div className="space-y-1">
                {topFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="flex items-center">
                      {feature.icon && <span className="mr-1">{feature.icon}</span>}
                      <span>{feature.name}</span>
                    </span>
                    <span className="font-medium text-sky-600">{feature.impact}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SchedulingCalculator = () => {
  const { isRoosterBranded, hasROIAnalysis, hasAdvancedSettings } = useVersion();
  // PDF report logic moved to separate module

  // Add custom styles for tooltips and form fields
  const customStyles = `
    /* Tooltip styles */
    .tooltip-container {
      position: relative;
      display: inline-block;
    }
    .tooltip {
      visibility: hidden;
      position: fixed;
      width: 270px;
      background-color: white;
      color: #333;
      text-align: left;
      padding: 12px;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.2s;
    }
    .tooltip-container:hover .tooltip {
      visibility: visible;
      opacity: 1;
    }
    
    /* Sticky progress bar styles - only apply in main application */
    .progress-bar-sticky {
      position: sticky;
      top: 0;
      z-index: 40;
      background-color: #f9fafb; /* Light gray background to match the app background */
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
      margin-bottom: 2rem;
      transition: all 0.3s ease;
      border-bottom: 1px solid transparent;
    }
    
    /* Apply enhanced styles when page is scrolled */
    .progress-bar-sticky.scrolled {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      border-bottom: 1px solid #e5e7eb;
      background-color: white;
    }
    
    /* Ensure the circles have white backgrounds */
    .progress-bar-sticky .rounded-full {
      box-shadow: 0 0 0 4px #f9fafb;
    }
    
    .progress-bar-sticky.scrolled .rounded-full {
      box-shadow: 0 0 0 4px white;
    }
    
    /* Hide number input spinners/arrows and prevent scroll wheel changes */
    input[type=number]::-webkit-inner-spin-button, 
    input[type=number]::-webkit-outer-spin-button { 
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      margin: 0; 
    }
    input[type=number] {
      -moz-appearance: textfield;
    }
    /* Globally disable mouse wheel events on number inputs */
    input[type=number] {
      pointer-events: auto; /* Allow other mouse events */
    }
    
    /* Improve focus highlighting for better UX */
    input:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.4);
      border-color: transparent;
    }
    
    /* Enhanced input field styles for better select-all behavior */
    input.auto-select {
      cursor: text;
      user-select: all; 
    }
    input.auto-select:focus {
      user-select: all;
      -webkit-user-select: all;
      -moz-user-select: all;
      -ms-user-select: all;
    }
    
    /* Smooth animations for expandable rows */
    .expandable-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out;
      opacity: 0;
    }
    .expandable-content.expanded {
      max-height: 2000px; /* Increased to accommodate all features */
      opacity: 1;
      padding-top: 1rem;
      transition: max-height 0.3s ease-in, opacity 0.3s ease-in, padding 0.3s ease-in;
    }
    
    /* Pulse animation for lowest score component */
    @keyframes pulse-sky {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
    .pulse-animation {
      animation: pulse-sky 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
  `;

  // Current step in the calculator
  const [currentStep, setCurrentStep] = useState<CalculatorStep>(CalculatorStep.OrganizationDetails);
  // Track the highest step the user has reached
  const [highestStepReached, setHighestStepReached] = useState<CalculatorStep>(CalculatorStep.OrganizationDetails);
  
  // Session management state
  const [sessionId, setSessionId] = useState<string>('');
  const [leadSource, setLeadSource] = useState<string | undefined>(undefined);
  const [sessionSaved, setSessionSaved] = useState<boolean>(false);
  const [sessionSaving, setSessionSaving] = useState<boolean>(false);
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());
  const [privacyConsent, setPrivacyConsent] = useState<boolean>(false);
  // PDF URL state removed - using HTML report only
  const [reportFinalized, setReportFinalized] = useState<boolean>(false);
  // Finalization ID is now generated locally when needed, not stored in state
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);
  const [editingReport, setEditingReport] = useState<boolean>(false);
  const [originalReportId, setOriginalReportId] = useState<string | null>(null);
  
  // State for button loading states
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  
  // State for expanded components in TAS Score view
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());
  const [autoExpandComplete, setAutoExpandComplete] = useState(false);
  
  // Track if we've done the initial ROI save
  const hasInitialROISave = useRef(false);
  const [showAllFeatures, setShowAllFeatures] = useState<Set<string>>(new Set());
  
  // State for email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [reportHTMLContent, setReportHTMLContent] = useState<string | null>(null);
  
  // Input mode state for Annual Hires vs FTE toggle
  const [inputMode, setInputMode] = useState<'annualHires' | 'fte'>('annualHires');
  const [fteCount, setFteCount] = useState<string>('');

  // Help modal state
  const [showHiringCalculator, setShowHiringCalculator] = useState(false);
  const [calculationBasis, setCalculationBasis] = useState<any>(null);
  const [helpModalField, setHelpModalField] = useState<string | null>(null);
  
  // Track which fields have been touched by the user using a Set for better performance and cleaner code

  // Tracking states for calculator returns
  const [isReturnVisit, setIsReturnVisit] = useState<boolean>(false);
  const [initialFormData, setInitialFormData] = useState<CalculatorFormData | null>(null);
  const [hasTrackedReturn, setHasTrackedReturn] = useState<boolean>(false);
  const [hasTrackedEdit, setHasTrackedEdit] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Activity tracking for accurate engagement time measurement
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const [isUserActive, setIsUserActive] = useState<boolean>(true);
  const [activeTimeAccumulated, setActiveTimeAccumulated] = useState<number>(0);
  const [lastActiveCheckpoint, setLastActiveCheckpoint] = useState<number>(Date.now());
  const IDLE_TIMEOUT = 90000; // 90 seconds of inactivity before pausing

  // Refs for time tracking that persist across re-renders
  const lastTrackedTotalRef = useRef<number>(0);
  const activeTimeAccumulatedRef = useRef<number>(0);
  const isUserActiveRef = useRef<boolean>(true);
  const lastActiveCheckpointRef = useRef<number>(Date.now());
  
  // Removed local dropdown state - now handled by IndustryDropdown component

  // Helper function to adjust metrics based on scheduling methods
  // Wrapped in useCallback to prevent recreation on every render
  const adjustMetricsForSchedulingMethod = useCallback((baseValue: number, metricType: string, schedulingMethods: string[]): number => {
    if (!schedulingMethods || schedulingMethods.length === 0) return baseValue;
    // Define adjustment factors for different metrics and scheduling methods
    const adjustments: { [key: string]: { [method: string]: number } } = {
      avgConfirmationTime: {
        manual: 1.5,              // 50% worse (longer) for manual
        scheduling_links: 1.15,   // 15% worse for scheduling links
        ats_native: 1.0,          // Baseline for ATS-native
        scheduling_platform: 0.85 // 15% better for platforms
      },
      adminTimePerHire: {
        manual: 1.6,              // 60% worse (more time) for manual
        scheduling_links: 1.2,    // 20% worse for scheduling links
        ats_native: 0.95,         // 5% better for ATS-native
        scheduling_platform: 0.75 // 25% better for platforms
      },
      reschedulingRate: {
        manual: 1.4,              // 40% worse (higher rate) for manual
        scheduling_links: 1.1,    // 10% worse for scheduling links
        ats_native: 0.95,         // 5% better for ATS-native
        scheduling_platform: 0.8  // 20% better for platforms
      },
      interviewNoShowRate: {
        manual: 1.3,              // 30% worse (higher rate) for manual
        scheduling_links: 1.1,    // 10% worse for scheduling links
        ats_native: 0.95,         // 5% better for ATS-native
        scheduling_platform: 0.75 // 25% better for platforms
      },
      satisfaction: {
        manual: 0.8,              // 20% worse (lower satisfaction) for manual
        scheduling_links: 0.95,   // 5% worse for scheduling links
        ats_native: 1.05,         // 5% better for ATS-native
        scheduling_platform: 1.15 // 15% better for platforms
      }
    };
    
    // Get adjustments for all selected methods
    const methodAdjustments = schedulingMethods.map(method => 
      adjustments[metricType]?.[method] || 1
    );
    
    if (methodAdjustments.length === 1) {
      // Single method - use its adjustment directly
      return Math.round(baseValue * methodAdjustments[0]);
    }
    
    // Multiple methods - use weighted average (worst gets more weight)
    const worstAdjustment = Math.max(...methodAdjustments);
    const bestAdjustment = Math.min(...methodAdjustments);
    
    // 70% weight to worst method, 30% to best, with complexity penalty
    const complexityPenalty = (schedulingMethods.length - 1) * 0.03;
    const weightedAdjustment = (worstAdjustment * 0.7) + (bestAdjustment * 0.3) + complexityPenalty;
    
    return Math.round(baseValue * weightedAdjustment);
  }, []);

  // Helper function to apply defaults only to untouched fields
  // This respects user input and only updates fields they haven't modified
  const applyDefaultsToUntouchedFields = useCallback((defaults: any) => {
    if (!defaults || Object.keys(defaults).length === 0) return;
    
    setFormData(prevData => {
      const updated = { ...prevData };
      
      // Only update fields that haven't been touched by the user
      Object.entries(defaults).forEach(([key, value]) => {
        if (!touchedFields.has(key) && key !== 'industry' && key !== 'schedulingMethods') {
          (updated as any)[key] = value;
        }
      });
      
      return updated;
    });
  }, [touchedFields]);

  // Form state - initialize with empty values for organization details
  const [formData, setFormData] = useState<FormData>({
    // Contact Information
    organizationName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    contactTitle: "",
    
    // Organization Details
    industry: "",  // Empty - user must select
    annualHires: "", // Empty string - user must enter
    schedulingMethods: [], // Empty - user must select at least one
    // TAS Component Inputs - will be populated when industry is selected
    avgConfirmationTime: 0,
    adminTimePerHire: 0,
    candidateSatisfaction: 0,
    interviewerSatisfaction: 0,
    reschedulingRate: 0,
    interviewerAvailability: 0,
    interviewNoShowRate: 0,
    // Other metrics - will be populated when industry is selected
    timeToFill: 0,
    costPerHire: 0,
    dailyVacancyCost: 0,
    avgSalary: 0,
    // Improvement goal - will be set to reach top performer level when TAS is calculated
    tasImprovement: 20, // Default, will be updated to reach top performer mark
    // Currency - default to USD
    currency: 'USD',
    // Calculator path - will be set on component mount
    calculatorPath: window.location.pathname
  });

  // Get current industry defaults - memoized to avoid recalculation
  const currentIndustryDefaults = useMemo(() => {
    if (!formData.industry) return null;
    return INDUSTRY_DEFAULTS[formData.industry as IndustryName] || null;
  }, [formData.industry]);


  // Create debounced ROI save function
  const debouncedROISave = useMemo(() => {
    return debounce(async (sessionId: string, formData: any, results: any) => {
      if (!sessionId || !isFirebaseConfigured()) {
        return;
      }
      
      try {
        // Save checkpoint with Step 4 and include ROI calculations
        await saveCheckpoint(
          sessionId, 
          CalculatorStep.ROIAnalysis, 
          formData, 
          results, 
          leadSource, 
          editingReport, 
          originalReportId || undefined
        );
      } catch (error) {
        console.error('Failed to auto-save ROI calculations:', error);
      }
    }, 2000); // 2 second debounce
  }, [leadSource, editingReport, originalReportId]);

  // Results state
  const [results, setResults] = useState<CalculatorResults>({
    currentTAS: 0,
    projectedTAS: 0,
    tasComponents: {
      schedulingVelocity: 0,
      schedulingEfficiency: 0,
      candidateExperience: 0,
      schedulingIntegrity: 0,
      schedulingUtilization: 0
    },
    savings: {
      timeToFillSavings: 0,
      costPerHireSavings: 0,
      productivityGain: 0,
      schedulingTimeSavings: 0,
      noShowReductionSavings: 0,
      totalAnnualSavings: 0
    },
    metrics: {
      current: {
        timeToFill: 0,
        costPerHire: 0,
        vacancyCost: 0
      },
      improved: {
        timeToFill: 0,
        costPerHire: 0,
        vacancyCost: 0
      }
    },
    roi: 0,
    annualRoi: 0
  });

  // Removed unused availabilityManuallyChanged state
  
  // State for collapsible implementation panel
  const [implementationPanelOpen, setImplementationPanelOpen] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [investmentApproach, setInvestmentApproach] = useState<'minimal' | 'standard' | 'comprehensive'>('standard');
  
  // State for collapsible advanced settings panels
  const [timelineSettingsExpanded, setTimelineSettingsExpanded] = useState(false);
  const [realizationFactorsExpanded, setRealizationFactorsExpanded] = useState(false);
  
  // Track manual changes to prevent automatic updates from scenario changes
  const [timelineManuallyChanged, setTimelineManuallyChanged] = useState(false);
  const [effortManuallyChanged, setEffortManuallyChanged] = useState(false);
  
  // Time-to-Value state management
  const {
    timeToValue,
    attributionFactors,
    implementationInvestment,
    scenarioMode,
    pilotMode,
    updateTimeToValue,
    updateAttributionFactors,
    updateImplementationInvestment,
    setScenarioMode,
    setPilotMode,
    applyScenarioPreset,
  } = useTimeToValue(formData.industry, Number(formData.annualHires));
  
  // Phone validation state
  const [phoneError, setPhoneError] = useState<string>('');
  
  // Compute adjusted defaults based on industry and scheduling methods
  // This replaces the complex useEffect/applyIndustryDefaults pattern
  const adjustedDefaults = useMemo(() => {
    if (!formData.industry) return {};
    
    const baseDefaults = INDUSTRY_DEFAULTS[formData.industry as IndustryName];
    if (!baseDefaults) return {};
    
    const schedulingMethods = formData.schedulingMethods || [];
    
    // If no scheduling methods selected, return base defaults
    if (schedulingMethods.length === 0) return baseDefaults;
    
    // Return adjusted values for scheduling-dependent metrics
    return {
      ...baseDefaults,
      avgConfirmationTime: adjustMetricsForSchedulingMethod(
        baseDefaults.avgConfirmationTime,
        'avgConfirmationTime',
        schedulingMethods
      ),
      adminTimePerHire: adjustMetricsForSchedulingMethod(
        baseDefaults.adminTimePerHire,
        'adminTimePerHire',
        schedulingMethods
      ),
      reschedulingRate: adjustMetricsForSchedulingMethod(
        baseDefaults.reschedulingRate,
        'reschedulingRate',
        schedulingMethods
      ),
      interviewNoShowRate: adjustMetricsForSchedulingMethod(
        baseDefaults.interviewNoShowRate,
        'interviewNoShowRate',
        schedulingMethods
      ),
      candidateSatisfaction: Math.min(10, Math.max(1, adjustMetricsForSchedulingMethod(
        baseDefaults.candidateSatisfaction,
        'satisfaction',
        schedulingMethods
      ))),
      interviewerSatisfaction: Math.min(10, Math.max(1, adjustMetricsForSchedulingMethod(
        baseDefaults.interviewerSatisfaction,
        'satisfaction',
        schedulingMethods
      ))),
      interviewerAvailability: Math.min(10, Math.max(1, adjustMetricsForSchedulingMethod(
        baseDefaults.interviewerAvailability,
        'satisfaction',
        schedulingMethods
      )))
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.industry, formData.schedulingMethods.sort().join(','), adjustMetricsForSchedulingMethod]);
  
  // Helper function to clear report window when data changes
  const clearPDFAndReportWindow = useCallback(() => {
    // Clear the report window reference to force new generation
    if ((window as any).reportWindow) {
      const reportWindow = (window as any).reportWindow;
      if (!reportWindow.closed) {
        reportWindow.close();
      }
      (window as any).reportWindow = null;
    }
  }, []);

  // Load report data for editing
  const loadReportForEditing = useCallback(async (reportId: string) => {
    try {
      const report = await getReportById(reportId);
      if (report) {
        // Track this as a return visit
        setIsReturnVisit(true);

        // Store initial form data to detect edits
        setInitialFormData(report.formData);

        // Track calculator return using the original sessionId
        const userId = report.formData.contactEmail ?
          await createUserDocumentId(report.formData.contactEmail) : undefined;

        // Use the original sessionId from the report for tracking
        const originalSessionId = report.sessionId || report.reportId; // Fallback to reportId for old reports
        if (originalSessionId && !hasTrackedReturn) {
          await trackCalculatorReturn(originalSessionId, userId, false);
          setHasTrackedReturn(true);
        }
        // Restore ALL form fields exactly as they were saved
        const restoredFormData: FormData = {
          organizationName: report.formData.organizationName,
          contactName: report.formData.contactName,
          contactEmail: report.formData.contactEmail,
          contactPhone: report.formData.contactPhone || '',
          contactTitle: report.formData.contactTitle,
          industry: report.formData.industry,
          annualHires: report.formData.annualHires,
          schedulingMethods: report.formData.schedulingMethods || [], // Use saved value or empty array
          avgConfirmationTime: report.formData.avgConfirmationTime,
          adminTimePerHire: report.formData.adminTimePerHire,
          candidateSatisfaction: report.formData.candidateSatisfaction,
          interviewerSatisfaction: report.formData.interviewerSatisfaction,
          reschedulingRate: report.formData.reschedulingRate,
          interviewerAvailability: report.formData.interviewerAvailability,
          interviewNoShowRate: report.formData.interviewNoShowRate,
          timeToFill: report.formData.timeToFill,
          costPerHire: report.formData.costPerHire,
          dailyVacancyCost: report.formData.dailyVacancyCost,
          avgSalary: report.formData.avgSalary,
          tasImprovement: report.formData.tasImprovement,
          currency: report.formData.currency || 'USD'
        };
        
        setFormData(restoredFormData);
        
        // Restore FTE and input mode if they were saved
        if (report.formData.inputMode) {
          setInputMode(report.formData.inputMode);
          if (report.formData.inputMode === 'fte' && report.formData.fteCount) {
            setFteCount(String(report.formData.fteCount));
          }
        }
        
        // Also restore time-to-value settings if they exist
        if (report.formData.timeToValue) {
          updateTimeToValue(report.formData.timeToValue);
        }
        if (report.formData.attributionFactors) {
          updateAttributionFactors(report.formData.attributionFactors);
        }
        if (report.formData.implementationInvestment) {
          updateImplementationInvestment(report.formData.implementationInvestment);
        }
        if (report.formData.scenarioMode) {
          setScenarioMode(report.formData.scenarioMode);
        }
        if (report.formData.pilotMode !== undefined) {
          setPilotMode(report.formData.pilotMode);
        }

        // Restore calculation results with validation and error handling
        try {
          if (report.results && report.results.currentTAS > 0) {
            // Valid results exist, restore them
            setResults(report.results);
          } else if (report.formData) {
            // Old report without results - attempt to calculate TAS from the restored form data
            setTimeout(() => {
              // Only calculate if we have necessary form data
              if (restoredFormData.avgConfirmationTime && restoredFormData.adminTimePerHire) {
                const { score, components } = calculateTAS();
                if (score > 0) {
                  const projectedScore = score + getNumericValue(restoredFormData.tasImprovement, 20);
                  setResults(prev => ({
                    ...prev,
                    currentTAS: score,
                    projectedTAS: projectedScore,
                    tasComponents: components
                  }));
                }
              }
            }, 100);
          }
        } catch (error) {
          console.error('Failed to restore calculation results:', error);
          // Continue with default zero values from initial state
        }

        // Set editing flags
        setEditingReport(true);
        setOriginalReportId(reportId);

        // Store the original sessionId for tracking
        if (report.sessionId) {
          (window as any).originalSessionId = report.sessionId;
        }

        // Set both currentStep and highestStepReached to ROI Analysis (Step 4)
        // since this is a completed report
        setCurrentStep(CalculatorStep.ROIAnalysis);
        setHighestStepReached(CalculatorStep.ROIAnalysis);

        // Clear the URL parameter to avoid reloading on every render
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        alert('Report not found. Starting a new calculation.');
      }
    } catch (error) {
      console.error('Error loading report for editing:', error);
      alert('Error loading report. Starting a new calculation.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTimeToValue, updateAttributionFactors, updateImplementationInvestment, setScenarioMode, setPilotMode, hasTrackedReturn]);

  // Session persistence configuration
  const SESSION_KEY = 'tas-calculator-session';
  const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

  // Initialize session on component mount and check for editReport/emailReport parameters
  useEffect(() => {
    const initSession = async () => {
      // Check for URL parameters
      const params = new URLSearchParams(window.location.search);
      const editReportId = params.get('editReport');
      const emailReportId = params.get('emailReport');
      const source = params.get('source');

      // Capture lead source if provided
      if (source) {
        setLeadSource(source);
      }

      // Load report for editing if needed
      let isEditMode = false;
      if (editReportId) {
        // Load report for editing
        await loadReportForEditing(editReportId);
        isEditMode = true;
      } else if (emailReportId) {
        // Load report and open email dialog
        await loadReportForEditing(emailReportId);
        isEditMode = true;
        // Open email dialog after report is loaded
        setTimeout(() => setEmailDialogOpen(true), 500);
      }

      // Session ID generation logic
      let sessionIdToUse: string;
      let isNewSession = true;

      // If in edit mode, ALWAYS create a new session for the editor
      if (isEditMode) {
        sessionIdToUse = generateSessionId();
        isNewSession = true;
      } else {
        // Normal session logic - check localStorage
        const storedSession = localStorage.getItem(SESSION_KEY);

        if (storedSession) {
          try {
            const { id, timestamp, leadSource: storedLeadSource, userEmail } = JSON.parse(storedSession);
            const age = Date.now() - timestamp;

            // Check if session belongs to current user (if we have their email)
            const currentUserEmail = formData.contactEmail;
            // Only reuse session if:
            // 1. Both emails exist and match, OR
            // 2. Neither email exists (anonymous session continuing)
            const sessionBelongsToCurrentUser =
              (userEmail && currentUserEmail && userEmail === currentUserEmail) ||
              (!userEmail && !currentUserEmail);

            if (age < SESSION_TIMEOUT && sessionBelongsToCurrentUser) {
              // Reuse existing session only if it's not expired and belongs to current user
              sessionIdToUse = id;
              isNewSession = false;

              // If new lead source is provided, update it
              if (source && source !== storedLeadSource) {
                // This is a new visit with a different lead source, create new session
                sessionIdToUse = generateSessionId();
                isNewSession = true;
              }
            } else {
              // Session expired or belongs to different user, create new one
              sessionIdToUse = generateSessionId();
            }
          } catch (error) {
            // Invalid stored session, create new one
            sessionIdToUse = generateSessionId();
          }
        } else {
          // No stored session, create new one
          sessionIdToUse = generateSessionId();
        }
      }

      // Store/update session in localStorage with user email for validation
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        id: sessionIdToUse,
        timestamp: Date.now(),
        leadSource: source || null,
        userEmail: formData.contactEmail || null  // Store user email to detect user changes
      }));

      setSessionId(sessionIdToUse);

      // Only initialize analytics for new sessions
      if (isNewSession) {
        await initializeAnalyticsSession(sessionIdToUse, source || undefined);
      } else {
        // Track return visit for existing session
        await trackCalculatorReturn(sessionIdToUse);
      }
    };

    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track edits when form data changes from initial
  useEffect(() => {
    if (isReturnVisit && initialFormData && !hasTrackedEdit) {
      // Check if form data has changed from initial
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);

      if (hasChanged) {
        // Track the edit
        (async () => {
          const userId = formData.contactEmail ?
            await createUserDocumentId(formData.contactEmail) : undefined;

          // Get original sessionId if available
          const originalSessionId = (window as any).originalSessionId || originalReportId;
          if (originalSessionId) {
            await trackCalculatorReturn(originalSessionId, userId, true); // true = isEdit
            setHasTrackedEdit(true);
          }
        })();
      }
    }
  }, [formData, initialFormData, isReturnVisit, hasTrackedEdit, originalReportId]);

  // Handle user activity for engagement tracking
  const handleUserActivity = useCallback(() => {
    const now = Date.now();

    // If transitioning from idle to active, reset the checkpoint
    if (!isUserActive) {
      setIsUserActive(true);
      setLastActiveCheckpoint(now);
    }

    setLastActivityTime(now);
  }, [isUserActive]);

  // Set up activity event listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'input', 'change'];

    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    // Initial activity
    handleUserActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  // Check for idle state and accumulate active time before going idle
  useEffect(() => {
    const idleCheck = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTime;

      if (isUserActive && timeSinceActivity > IDLE_TIMEOUT) {
        // User just became idle - accumulate the active time before pausing
        const activeTime = Math.floor((lastActivityTime - lastActiveCheckpoint) / 1000);
        if (activeTime > 0) {
          setActiveTimeAccumulated(prev => prev + activeTime);
        }
        setIsUserActive(false);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(idleCheck);
  }, [lastActivityTime, isUserActive, lastActiveCheckpoint, IDLE_TIMEOUT]);

  // Keep refs in sync with state values
  useEffect(() => {
    activeTimeAccumulatedRef.current = activeTimeAccumulated;
    isUserActiveRef.current = isUserActive;
    lastActiveCheckpointRef.current = lastActiveCheckpoint;
  }, [activeTimeAccumulated, isUserActive, lastActiveCheckpoint]);

  // Activity-based time tracking - only tracks actual engagement time
  useEffect(() => {
    if (!sessionId) return;

    const trackInterval = setInterval(() => {
      // Calculate total active time using refs
      let currentTotal = activeTimeAccumulatedRef.current;

      // If user is currently active, add the current session time
      if (isUserActiveRef.current) {
        const currentSessionTime = Math.floor((Date.now() - lastActiveCheckpointRef.current) / 1000);
        currentTotal += currentSessionTime;
      }

      // Only track the incremental difference
      const incrementalTime = currentTotal - lastTrackedTotalRef.current;

      if (incrementalTime > 0) {
        (async () => {
          const userId = formData.contactEmail ?
            await createUserDocumentId(formData.contactEmail) : undefined;
          await trackTimeSpent(sessionId, incrementalTime, userId);
        })();
        lastTrackedTotalRef.current = currentTotal;
      }
    }, 30000); // Every 30 seconds

    // Track remaining active time on page unload
    const handleBeforeUnload = () => {
      // Calculate final active time
      let finalTotal = activeTimeAccumulatedRef.current;

      if (isUserActiveRef.current) {
        const finalSessionTime = Math.floor((Date.now() - lastActiveCheckpointRef.current) / 1000);
        finalTotal += finalSessionTime;
      }

      const remainingTime = finalTotal - lastTrackedTotalRef.current;

      if (remainingTime > 0) {
        // Use sendBeacon for reliability during page unload
        const userId = formData.contactEmail ?
          btoa(formData.contactEmail.toLowerCase().trim()) : undefined;

        // Create a simple tracking payload
        const payload = JSON.stringify({
          sessionId,
          timeSpent: remainingTime,
          userId
        });

        // Try to send via beacon (fire-and-forget, best effort)
        navigator.sendBeacon?.('/api/track-time', payload);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      clearInterval(trackInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Final time tracking on unmount
      let finalTotal = activeTimeAccumulatedRef.current;

      if (isUserActiveRef.current) {
        const finalSessionTime = Math.floor((Date.now() - lastActiveCheckpointRef.current) / 1000);
        finalTotal += finalSessionTime;
      }

      const remainingTime = finalTotal - lastTrackedTotalRef.current;

      if (sessionId && remainingTime > 0) {
        (async () => {
          const userId = formData.contactEmail ?
            await createUserDocumentId(formData.contactEmail) : undefined;
          await trackTimeSpent(sessionId, remainingTime, userId);
        })();
      }
    };
  }, [sessionId, formData.contactEmail]);

  // Listen for messages from the report window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check if this is an email report message
      if (event.data && event.data.type === 'SEND_EMAIL_REPORT') {
        // Store the HTML content
        setReportHTMLContent(event.data.htmlContent);
        // Open the email dialog
        setEmailDialogOpen(true);
      } else if (event.data && event.data.type === 'openEmailDialog') {
        // Handle email dialog request from HTML report
        // The report already has all the data, so we just open the dialog
        // The dialog will use the current form data and results
        setEmailDialogOpen(true);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  
  // Pre-fill form with industry defaults when industry is selected
  useEffect(() => {
    // Skip if no industry selected or no defaults available
    if (!formData.industry || !adjustedDefaults || Object.keys(adjustedDefaults).length === 0) return;

    // Type guard to ensure adjustedDefaults has the right shape
    const defaults = adjustedDefaults as any;

    // Pre-fill with defaults when industry changes
    // Pre-fill all metric fields with industry defaults
    setFormData(prev => ({
      ...prev,
      avgConfirmationTime: defaults.avgConfirmationTime || prev.avgConfirmationTime,
      adminTimePerHire: defaults.adminTimePerHire || prev.adminTimePerHire,
      dailyVacancyCost: defaults.dailyVacancyCost || prev.dailyVacancyCost,
      reschedulingRate: defaults.reschedulingRate || prev.reschedulingRate,
      interviewNoShowRate: defaults.interviewNoShowRate || prev.interviewNoShowRate,
      candidateSatisfaction: defaults.candidateSatisfaction || prev.candidateSatisfaction,
      interviewerSatisfaction: defaults.interviewerSatisfaction || prev.interviewerSatisfaction,
      interviewerAvailability: defaults.interviewerAvailability || prev.interviewerAvailability,
      timeToFill: defaults.timeToFill || prev.timeToFill,
      costPerHire: defaults.costPerHire || prev.costPerHire,
      avgSalary: defaults.avgSalary || prev.avgSalary
    }));
  }, [formData.industry, adjustedDefaults]);
  
  // Handle industry changes when in FTE mode
  useEffect(() => {
    const fteNum = parseInt(fteCount) || 0;
    if (inputMode === 'fte' && fteNum > 0) {
      // Recalculate annual hires with new industry's turnover rate
      const annualHires = convertFTEtoAnnualHires(fteNum, formData.industry as IndustryName);
      setFormData(prev => ({
        ...prev,
        annualHires: annualHires
      }));
    }
  }, [formData.industry, inputMode, fteCount]);
  
  // Apply adjusted defaults when they change, but preserve user input
  useEffect(() => {
    // Only apply if we're on Step 2 (Current Metrics) and have defaults
    if (currentStep === CalculatorStep.CurrentMetrics && adjustedDefaults && Object.keys(adjustedDefaults).length > 0) {
      applyDefaultsToUntouchedFields(adjustedDefaults);
    }
  }, [adjustedDefaults, currentStep, applyDefaultsToUntouchedFields]);
  
  // Dropdown click handling moved to IndustryDropdown component

  // Handle checkbox changes for scheduling methods
  const handleSchedulingMethodChange = (method: string) => {
    setFormData(prev => {
      const currentMethods = prev.schedulingMethods || [];
      const newMethods = currentMethods.includes(method)
        ? currentMethods.filter(m => m !== method)
        : [...currentMethods, method];
      
      // Ensure at least one method is selected
      if (newMethods.length === 0) {
        return prev;
      }
      
      return {
        ...prev,
        schedulingMethods: newMethods
      };
    });
  };

  // Handle help modal
  const handleHelpClick = (fieldName: string) => {
    setHelpModalField(fieldName);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // List of numeric fields that need sanitization
    const numericFields = [
      'annualHires', 'timeToFill', 'costPerHire', 'dailyVacancyCost', 'avgSalary',
      'avgConfirmationTime', 'adminTimePerHire', 'reschedulingRate', 'interviewNoShowRate',
      'candidateSatisfaction', 'interviewerSatisfaction', 'interviewerAvailability'
    ];

    // Sanitize numeric inputs by removing commas and spaces
    let processedValue = value;
    if (numericFields.includes(name) && typeof value === 'string') {
      processedValue = sanitizeNumericInput(value);
    }

    // No longer tracking benchmark usage - keeping it simple
    
    // Update the form data
    setFormData(prevData => ({
      ...prevData,
      [name]: processedValue
    }));
    
    // Real-time phone validation
    if (name === 'contactPhone') {
      const error = validatePhoneNumber(value);
      setPhoneError(error);
    }
    
    // Track that this field has been touched by the user
    // Skip tracking for industry as it's a special case
    if (name !== 'industry') {
      setTouchedFields(prev => new Set(prev).add(name));
    }
  };

  // Helper function to ensure we have numeric values for calculations
  const getNumericValue = (value: number | string, defaultValue = 0): number => {
    if (typeof value === 'string') {
      // Remove commas and spaces before converting to number
      const cleanValue = value.replace(/[,\s]/g, '');
      return cleanValue === '' ? defaultValue : Number(cleanValue);
    }
    return value;
  };

  // Helper function to sanitize numeric input (remove commas, spaces, currency symbols)
  const sanitizeNumericInput = (value: string): string => {
    // Remove commas, spaces, and dollar signs, but keep decimal points and negative signs
    return value.replace(/[,\s$]/g, '');
  };

  // Helper function to format numbers with commas for display
  const formatWithCommas = (value: number | string): string => {
    // If empty string or undefined, return empty string
    if (value === '' || value === undefined || value === null) return '';
    
    // Convert to number and back to string with commas
    const numValue = typeof value === 'string' ? parseFloat(sanitizeNumericInput(value)) : value;
    
    // Check if it's a valid number
    if (isNaN(numValue)) return '';
    
    // Format with commas, preserving decimals
    const parts = numValue.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };
  
  // Calculate minimum FTE based on industry to ensure at least 100 annual hires
  const getMinFTE = (industry: string): number => {
    if (!industry) return 500;
    const defaults = INDUSTRY_DEFAULTS[industry as IndustryName];
    if (!defaults) return 500;
    // Calculate FTE needed for 100 annual hires based on turnover rate
    // FTE = Annual Hires / Turnover Rate
    const requiredFTE = Math.ceil(100 / defaults.turnoverRate);
    // Return the higher of 500 or the calculated requirement
    return Math.max(500, requiredFTE);
  };
  
  // Handle FTE input changes
  const handleFTEChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Sanitize the input to remove commas and spaces
    const sanitizedValue = sanitizeNumericInput(value);
    setFteCount(sanitizedValue);
    
    // Calculate and update annual hires
    const fte = parseInt(sanitizedValue) || 0;
    if (fte > 0) {
      const annualHires = convertFTEtoAnnualHires(fte, formData.industry as IndustryName);
      setFormData(prev => ({
        ...prev,
        annualHires: annualHires
      }));
    }
  };
  
  // Handle input mode toggle
  const handleInputModeChange = (mode: 'annualHires' | 'fte') => {
    // Prevent switching if user has already entered data
    if (mode === 'annualHires' && fteCount && parseInt(fteCount) > 0) {
      return; // Don't allow switch if Total Employees has a value
    }
    if (mode === 'fte' && formData.annualHires && Number(formData.annualHires) > 0) {
      return; // Don't allow switch if Annual Hires has a value
    }
    
    setInputMode(mode);
    
    // Clear the other field when switching
    if (mode === 'fte') {
      setFormData(prev => ({ ...prev, annualHires: '' }));
      setFteCount('');
    } else {
      setFteCount('');
      setFormData(prev => ({ ...prev, annualHires: '' }));
    }
  };

  // Get multiplier based on current scheduling methods
  const getSchedulingMethodMultiplier = (methods: string[]): number => {
    if (!methods || methods.length === 0) return 0.7;
    
    const methodMultipliers: { [key: string]: number } = {
      'manual': 0.6,              // Manual coordination achieves only 60% of potential
      'scheduling_links': 0.7,    // Scheduling links achieve 70% of potential  
      'ats_native': 0.75,         // ATS-native scheduling achieves 75% of potential
      'scheduling_platform': 0.85 // Interview scheduling platforms achieve 85% of potential
    };
    
    // Get multipliers for all selected methods
    const multipliers = methods.map(method => methodMultipliers[method] || 0.7);
    
    if (multipliers.length === 1) {
      // Single method - use its multiplier directly
      return multipliers[0];
    }
    
    // Multiple methods - weighted average with complexity penalty
    const worstMultiplier = Math.min(...multipliers);
    const bestMultiplier = Math.max(...multipliers);
    
    // 70% weight to worst, 30% to best, minus complexity penalty
    const complexityPenalty = (methods.length - 1) * 0.03;
    const weightedMultiplier = (worstMultiplier * 0.7) + (bestMultiplier * 0.3) - complexityPenalty;
    
    return Math.max(0.5, weightedMultiplier); // Ensure minimum of 0.5
  };

  // Calculate TAS score
  const calculateTAS = () => {
    // Component 1: Scheduling Velocity (30%)
    const maxConfirmationTime = 120; // benchmark (hours) - 5 days is realistic worst case
    const avgConfirmationTime = getNumericValue(formData.avgConfirmationTime);
    const schedulingVelocity = Math.max(5, (1 - (avgConfirmationTime / maxConfirmationTime)) * 100); // Min 5% - even slow is better than nothing
    
    // Component 2: Scheduling Efficiency (25%)
    const maxAdminTime = 360; // benchmark (minutes) - 6 hours is realistic worst case
    const adminTimePerHire = getNumericValue(formData.adminTimePerHire);
    const schedulingEfficiency = Math.max(5, (1 - (adminTimePerHire / maxAdminTime)) * 100); // Min 5% - some efficiency exists even with high admin time
    
    // Component 3: Candidate Experience (20%)
    // Convert from 1-10 scale to percentage
    const candidateExperience = getNumericValue(formData.candidateSatisfaction) * 10;
    
    // Component 4: Scheduling Integrity (15% total)
    // 10% from rescheduling rate
    const maxRescheduleRate = 30; // benchmark (percentage) - typical bad performance
    const reschedulingRate = getNumericValue(formData.reschedulingRate);
    const rescheduleReliability = Math.max(0, (1 - (reschedulingRate / maxRescheduleRate)) * 100);
    
    // 5% from no-show rate
    const maxNoShowRate = 30; // benchmark (percentage) - typical bad performance
    const interviewNoShowRate = getNumericValue(formData.interviewNoShowRate);
    const noShowReliability = Math.max(0, (1 - (interviewNoShowRate / maxNoShowRate)) * 100);
    
    // Combine the rescheduling and no-show rates with their respective weights
    const schedulingIntegrity = (rescheduleReliability * 2/3) + (noShowReliability * 1/3);
    
    // Component 5: Scheduling Utilization (10% total)
    // 5% from interviewer availability
    // 5% from interviewer satisfaction
    // Convert from 1-10 scale to percentage
    // Interviewer availability scale 1-10 to 0-100% (5%)
    // Interviewer satisfaction scale 1-10 to 0-100% (5%)
    const interviewerAvailability = getNumericValue(formData.interviewerAvailability) * 10;
    const interviewerSatisfaction = getNumericValue(formData.interviewerSatisfaction) * 10;
    const schedulingUtilization = (interviewerAvailability + interviewerSatisfaction) / 2;
    
    // Calculate weighted TAS score directly without storing in unused variable 
    const rawScore = (
      (schedulingVelocity * 0.30) +
      (schedulingEfficiency * 0.25) +
      (candidateExperience * 0.20) +
      (schedulingIntegrity * 0.15) +
      (schedulingUtilization * 0.10)
    );
    
    // Apply curve adjustment to make the scale more stringent
    // This adjustment makes it harder to score in the "average" range
    // Scale adjustment: 
    // - Scores below 50 remain similar but slightly reduced
    // - Scores between 50-75 get scaled down more significantly
    // - Scores above 75 require near-perfect metrics
    let adjustedScore;
    if (rawScore < 50) {
      adjustedScore = rawScore * 0.9; // Slight reduction for low scores
    } else if (rawScore < 75) {
      // Progressive reduction for mid-range scores
      const reductionFactor = 0.9 - ((rawScore - 50) / 25) * 0.15;
      adjustedScore = rawScore * reductionFactor;
    } else {
      // Significant reduction for high scores, making excellence harder to achieve
      adjustedScore = 60 + (rawScore - 75) * 0.8;
    }
    
    // Apply scheduling methods multiplier to adjust score based on current process
    const schedulingMultiplier = getSchedulingMethodMultiplier(formData.schedulingMethods);
    const finalScore = adjustedScore * schedulingMultiplier;
    
    // Guard against NaN/Infinity before returning
    const safeScore = Math.round(finalScore);
    const finalSafeScore = isFinite(safeScore) ? safeScore : 0;
    
    // Ensure all component scores are safe
    const safeComponents = {
      schedulingVelocity: isFinite(schedulingVelocity) ? schedulingVelocity : 0,
      schedulingEfficiency: isFinite(schedulingEfficiency) ? schedulingEfficiency : 0,
      candidateExperience: isFinite(candidateExperience) ? candidateExperience : 0,
      schedulingIntegrity: isFinite(schedulingIntegrity) ? schedulingIntegrity : 0,
      schedulingUtilization: isFinite(schedulingUtilization) ? schedulingUtilization : 0
    };
    
    return {
      score: finalSafeScore,
      components: safeComponents
    };
  };

  // Get annual subscription cost based on tiered pricing
  const getAnnualSubscriptionCost = (annualHires: number): number => {
    const hires = Math.round(annualHires);
    
    if (hires < 240) return 35000;
    else if (hires < 400) return 40000;
    else if (hires < 600) return 50000;
    else if (hires < 1000) return 65000;
    else if (hires < 1400) return 75000;
    else if (hires < 2000) return 85000;
    else if (hires < 3000) return 95000;
    else if (hires < 4000) return 110000;
    else if (hires < 5000) return 125000;
    else if (hires < 6000) return 140000;
    else if (hires < 7000) return 155000;
    else if (hires < 8000) return 170000;
    else if (hires < 10000) return 185000;
    else if (hires < 15000) return 200000;
    else if (hires < 20000) return 215000;
    else return 250000;
  };

  // Calculate ROI based on TAS improvement with defensible assumptions
  const calculateROI = useCallback((
    currentTAS: number, 
    targetTAS: number, 
    currentScenarioMode?: string,
    currentFormData?: FormData  // Accept form data as parameter to avoid stale closure
  ) => {
    const tasImprovement = targetTAS - currentTAS;
    
    // Use passed form data or fall back to closure (avoids stale data issues)
    const data = currentFormData || formData;
    
    // Calculate improvement ratios (linear relationship to TAS improvement)
    // Dynamic maximum ensures ROI continues through the Rooster Booster Zone
    // Allows reaching top performer level + 5 points buffer
    const topPerformerScore = calculateTopPerformerTAS(data.industry);
    const maxTasImprovement = Math.max(30, topPerformerScore - currentTAS + 5);
    
    // Scenario mode adjusts base improvement expectations:
    // Conservative (0.8x): Lower confidence in achieving theoretical benefits
    // Moderate (1.0x): Balanced expectation based on typical results
    // Optimistic (1.2x): Higher confidence with strong organizational readiness
    const scenarioMultiplier = currentScenarioMode === 'conservative' ? 0.8 : 
                               currentScenarioMode === 'optimistic' ? 1.2 : 1.0;
    
    // Get numeric values for calculations
    const timeToFillValue = getNumericValue(data.timeToFill);
    const costPerHireValue = getNumericValue(data.costPerHire);
    const annualHiresValue = getNumericValue(data.annualHires);
    const dailyVacancyCostValue = getNumericValue(data.dailyVacancyCost);
    const avgSalaryValue = getNumericValue(data.avgSalary);
    const interviewNoShowRateValue = getNumericValue(data.interviewNoShowRate);
    
    // Calculate volume-based scaling factor for diminishing returns
    // Based on empirical research on economies of scale and organizational complexity
    const calculateVolumeScaling = (hires: number): number => {
      if (hires <= 100) return 1.0;    // Small business - full savings
      if (hires <= 500) return 0.98;   // SMB - minimal friction (2% reduction)
      if (hires <= 1000) return 0.92;  // Mid-market - noticeable complexity (8% reduction)
      if (hires <= 2500) return 0.85;  // Large mid-market - enterprise processes (15% reduction)
      if (hires <= 5000) return 0.75;  // Enterprise - significant challenges (25% reduction)
      if (hires <= 10000) return 0.65; // Large enterprise - global scale (35% reduction)
      return 0.55;                      // Mega-corp - maximum complexity (45% reduction)
    };
    
    // Calculate organizational complexity factor (additional dampening for large orgs)
    const calculateComplexityFactor = (hires: number): number => {
      if (hires <= 100) return 1.0;    // No additional complexity
      if (hires <= 500) return 1.0;    // Still straightforward
      if (hires <= 1000) return 0.98;  // Multiple departments (2% additional)
      if (hires <= 2500) return 0.96;  // Multiple locations (4% additional)
      if (hires <= 5000) return 0.94;  // Business units (6% additional)
      if (hires <= 10000) return 0.92; // Global operations (8% additional)
      return 0.90;                      // Maximum complexity (10% additional)
    };
    
    const volumeScaling = calculateVolumeScaling(annualHiresValue);
    const complexityFactor = calculateComplexityFactor(annualHiresValue);
    const combinedScaling = volumeScaling * complexityFactor;
    
    // Calculate improvement difficulty based on starting position (Weber-Fechner Law)
    // As organizations approach excellence, each increment becomes exponentially harder
    const calculateImprovementDifficulty = (currentTAS: number): number => {
      // Efficiency frontier at TAS 90 (world-class performance)
      const efficiencyFrontier = 90;
      
      // Logarithmic decay: improvement gets harder as you approach the frontier
      // This formula is based on empirical studies of process improvement
      const remainingPotential = Math.max(0, (efficiencyFrontier - currentTAS) / efficiencyFrontier);
      
      // Apply logarithmic scaling (defensible via operations research literature)
      // Using natural log for smooth decay curve
      return Math.log(1 + remainingPotential * Math.E) / Math.log(1 + Math.E);
    };
    
    // Empirical adjustment factors based on Six Sigma and Lean implementation studies
    // These multipliers are derived from published process improvement research
    // More conservative to ensure defensible ROI claims
    const getEmpiricalAdjustment = (currentTAS: number): number => {
      if (currentTAS < 30) return 0.80;   // 3σ below mean: 80% improvement potential
      if (currentTAS < 40) return 0.65;   // 2σ below mean: 65% potential
      if (currentTAS < 50) return 0.50;   // 1σ below mean: 50% potential
      if (currentTAS < 60) return 0.35;   // At mean: 35% potential
      if (currentTAS < 70) return 0.25;   // 1σ above mean: 25% potential
      if (currentTAS < 80) return 0.15;   // 2σ above mean: 15% potential
      return 0.10;                        // 3σ above mean: 10% potential
    };
    
    // Calculate combined adjustment for starting position
    const difficultyMultiplier = calculateImprovementDifficulty(currentTAS);
    const empiricalAdjustment = getEmpiricalAdjustment(currentTAS);
    const startingPositionAdjustment = difficultyMultiplier * empiricalAdjustment;
    
    // Adjusted improvement estimates with starting position consideration
    // Organizations with better starting positions achieve smaller percentage improvements
    const timeToFillReduction = Math.min(
      0.25 * (tasImprovement / maxTasImprovement) * scenarioMultiplier * startingPositionAdjustment, 
      0.30
    );
    const costPerHireReduction = Math.min(
      0.20 * (tasImprovement / maxTasImprovement) * scenarioMultiplier * startingPositionAdjustment, 
      0.25
    );
    const schedulingTimeReduction = Math.min(
      0.60 * (tasImprovement / maxTasImprovement) * scenarioMultiplier * startingPositionAdjustment, 
      0.70
    );
    const noShowRateReduction = Math.min(
      0.35 * (tasImprovement / maxTasImprovement) * scenarioMultiplier * startingPositionAdjustment, 
      0.40
    );
    
    // Calculate new metrics after improvement with proper rounding
    // Round time to fill to whole days
    const roundedTimeToFill = Math.round(timeToFillValue);
    const newTimeToFill = Math.round(roundedTimeToFill * (1 - timeToFillReduction));
    const newCostPerHire = costPerHireValue * (1 - costPerHireReduction);
    
    // Use actual annual hires
    const annualHires = annualHiresValue;
    const effectiveAnnualHires = annualHires;
    
    // Calculate annual savings with statistical scaling applied
    // Apply combined scaling factor to all savings components for diminishing returns
    const timeToFillSavings = (roundedTimeToFill - newTimeToFill) * dailyVacancyCostValue * effectiveAnnualHires * combinedScaling;
    const costPerHireSavings = (costPerHireValue - newCostPerHire) * effectiveAnnualHires * combinedScaling;
    
    // Calculate productivity gains with more conservative approach for large orgs
    // Base calculation with square root scaling for large organizations
    const productivityScaling = annualHires > 1000 ? Math.sqrt(1000 / annualHires) : 1.0;
    const baseProductivityGain = (roundedTimeToFill - newTimeToFill) * (avgSalaryValue / 250 * 0.08); // 8% of daily salary
    const productivityGain = Math.min(
      baseProductivityGain * effectiveAnnualHires * combinedScaling * productivityScaling,
      avgSalaryValue * 0.10 * effectiveAnnualHires * combinedScaling // Cap at 10% of total salary costs
    );
    
    // Calculate scheduling time savings with realistic caps for scale
    // Average recruiter salary = $65,000, hourly = ~$31.25
    // Hours per hire decreases with scale (efficiency gains)
    const hoursPerHire = annualHires <= 500 ? 3 : 
                         annualHires <= 2000 ? 2.5 : 
                         annualHires <= 5000 ? 2 : 1.5;
    const hourlyRecruiterCost = 65000 / 2080; // Annual salary / work hours
    const maxHoursSaved = annualHires <= 1000 ? 1.2 : 0.8; // Max hours saved per hire
    const schedulingTimeSavings = Math.min(
      schedulingTimeReduction * hoursPerHire * hourlyRecruiterCost * effectiveAnnualHires * combinedScaling,
      maxHoursSaved * hourlyRecruiterCost * effectiveAnnualHires * combinedScaling
    );
    
    // Calculate no-show reduction savings with realistic modeling
    // Average fully-loaded cost of interviewer time including opportunity cost
    const hourlyInterviewerCost = 175000 / 2080; // Fully-loaded annual cost / work hours
    const avgInterviewDuration = 1.5; // Average interview duration in hours (includes prep time)
    const interviewsPerHire = 4;
    const totalInterviews = effectiveAnnualHires * interviewsPerHire;
    
    // Round no-show rate to whole percent
    const roundedNoShowRate = Math.round(interviewNoShowRateValue);
    const currentNoShows = totalInterviews * (roundedNoShowRate / 100);
    const improvedNoShows = currentNoShows * (1 - noShowRateReduction);
    const noShowReductionSavings = Math.min(
      (currentNoShows - improvedNoShows) * hourlyInterviewerCost * avgInterviewDuration * combinedScaling,
      hourlyInterviewerCost * totalInterviews * 0.20 * combinedScaling // Cap at 20% of total interview time
    );
    
    // Use scaled savings directly
    const scaledTimeToFillSavings = timeToFillSavings;
    const scaledCostPerHireSavings = costPerHireSavings;
    const scaledProductivityGain = productivityGain;
    const scaledSchedulingTimeSavings = schedulingTimeSavings;
    const scaledNoShowReductionSavings = noShowReductionSavings;
    
    // Total is the sum of all components
    const totalAnnualSavings = scaledTimeToFillSavings + scaledCostPerHireSavings + 
                              scaledProductivityGain + scaledSchedulingTimeSavings + 
                              scaledNoShowReductionSavings;
    
    // Get subscription cost using unified function
    const tasAnnualCost = getAnnualSubscriptionCost(annualHires);
    
    // Use actual calculated savings
    const adjustedAnnualSavings = totalAnnualSavings;
    
    // Calculate ROI with sophisticated multi-factor capping
    let roi = ((adjustedAnnualSavings - tasAnnualCost) / tasAnnualCost) * 100;
    
    // Sophisticated ROI capping based on multiple factors
    const calculateMaxROI = (hires: number, subscriptionCost: number): number => {
      // Base cap varies by organization size
      let baseCap = 500; // Start with optimistic cap
      
      if (hires <= 500) {
        baseCap = 400; // Small org cap
      } else if (hires <= 2000) {
        baseCap = 300; // Medium org cap
      } else if (hires <= 5000) {
        baseCap = 200; // Large org cap
      } else {
        baseCap = 150; // Enterprise cap
      }
      
      // Further reduce based on subscription cost (larger deals = lower ROI)
      const costAdjustment = Math.max(0, (subscriptionCost - 50000) / 1000);
      baseCap = baseCap - costAdjustment;
      
      // Apply scenario multiplier
      const scenarioAdjustment = currentScenarioMode === 'conservative' ? 0.7 :
                                 currentScenarioMode === 'optimistic' ? 1.2 : 1.0;
      
      return Math.max(100, baseCap * scenarioAdjustment); // Never go below 100% ROI
    };
    
    const roiCap = calculateMaxROI(annualHires, tasAnnualCost);
    roi = Math.min(roi, roiCap);
    
    // Additional validation: Total savings should not exceed 7x subscription cost
    const savingsMultiplier = adjustedAnnualSavings / tasAnnualCost;
    if (savingsMultiplier > 7) {
      // Cap ROI at 600% (7x return means 600% ROI)
      roi = ((7 * tasAnnualCost - tasAnnualCost) / tasAnnualCost) * 100;
    }
    
    // Calculate annual ROI as a multiple
    const annualRoi = Math.min(adjustedAnnualSavings / tasAnnualCost, 7); // Cap at 7x
    
    return {
      currentMetrics: {
        timeToFill: roundedTimeToFill,
        costPerHire: costPerHireValue,
        vacancyCost: roundedTimeToFill * dailyVacancyCostValue
      },
      improvedMetrics: {
        timeToFill: newTimeToFill,
        costPerHire: newCostPerHire,
        vacancyCost: newTimeToFill * dailyVacancyCostValue,
        tasScore: targetTAS  // Add the improved TAS score
      },
      savings: {
        timeToFillSavings: scaledTimeToFillSavings,
        costPerHireSavings: scaledCostPerHireSavings,
        productivityGain: scaledProductivityGain,
        schedulingTimeSavings: scaledSchedulingTimeSavings,
        noShowReductionSavings: scaledNoShowReductionSavings,
        totalAnnualSavings
      },
      roi: roi,
      annualRoi: annualRoi
    };
  }, [formData]);

  // Handle initial form submission - calculate TAS score
  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set calculating state
    setIsCalculating(true);
    
    try {
      // Validate form before proceeding
      const validation = validateCurrentMetrics();
      if (!validation.isValid) {
        alert(`Please complete all required fields:\n\n${validation.errors.join('\n')}`);
        return;
      }
      
      // Small delay to show spinner
      await new Promise(resolve => setTimeout(resolve, 100));
    
    // Check if any fields are empty strings, and replace with 0
    const numericFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        if (key !== 'industry' && key !== 'schedulingMethods' && key !== 'contactPhone' && value === '') {
          return [key, 0];
        }
        return [key, value];
      })
    );
    
    // Update form data with zero values for empty fields
    setFormData(numericFormData as FormData);
    
    const { score: currentTAS, components } = calculateTAS();
    
    // Set default improvement to reach top performer level
    if (!formData.tasImprovement || formData.tasImprovement === 20) {
      const topPerformerScore = calculateTopPerformerTAS(formData.industry);
      const defaultImprovement = Math.max(20, topPerformerScore - currentTAS);
      setFormData(prev => ({...prev, tasImprovement: defaultImprovement}));
    }
    
    // Get numeric values for calculations
    const timeToFillValue = getNumericValue(formData.timeToFill);
    const costPerHireValue = getNumericValue(formData.costPerHire);
    const dailyVacancyCostValue = getNumericValue(formData.dailyVacancyCost);
    
    // Round time to fill to whole days
    const roundedTimeToFill = Math.round(timeToFillValue);
    
    const tasResults = {
      currentTAS,
      projectedTAS: currentTAS + getNumericValue(formData.tasImprovement, 20),
      tasComponents: components,
      metrics: {
        current: {
          timeToFill: roundedTimeToFill,
          costPerHire: costPerHireValue,
          vacancyCost: roundedTimeToFill * dailyVacancyCostValue
        },
        improved: {
          timeToFill: 0,
          costPerHire: 0,
          vacancyCost: 0
        }
      },
      ...(hasROIAnalysis && {
        savings: {
          timeToFillSavings: 0,
          costPerHireSavings: 0,
          productivityGain: 0,
          schedulingTimeSavings: 0,
          noShowReductionSavings: 0,
          totalAnnualSavings: 0
        },
        roi: 0,
        annualRoi: 0
      })
    };
    
    setResults(tasResults);
    
    // Save TAS results immediately so admin panel can see them
    if (sessionId && formData.contactEmail) {
      await saveCheckpoint(sessionId, CalculatorStep.TasScore, formData, tasResults, leadSource, editingReport, originalReportId || undefined);
    }
    
    // Advance to the TAS Score step
    setCurrentStep(CalculatorStep.TasScore);
    // Immediately jump to the top of the page
    window.scrollTo(0, 0);
    } finally {
      // Reset calculating state
      setIsCalculating(false);
    }
  };
  
  // Helper function to handle selection of input content
  const handleInputSelection = (e: React.FocusEvent<HTMLInputElement>) => {
    // Delay selection slightly to ensure it works across browsers
    setTimeout(() => {
      if (e.target) {
        e.target.select();
      }
    }, 0);
  };
  
  // Helper function to handle input clicks for selection
  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // Select all text when clicking anywhere in the input
    e.currentTarget.select();
  };
  

  // Phone number validation function
  const validatePhoneNumber = (phone: string): string => {
    if (!phone.trim()) return ''; // Optional field, no error if empty
    
    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Check for valid length (10-15 digits to accommodate international)
    if (cleanPhone.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    if (cleanPhone.length > 15) {
      return 'Phone number cannot exceed 15 digits';
    }
    
    // Basic format validation - accept common formats
    const phoneRegex = /^[+]?[1-9][\d\s\-()]{8,20}$/;
    if (!phoneRegex.test(phone.trim())) {
      return 'Please enter a valid phone number (e.g., (555) 123-4567, +1-555-123-4567)';
    }
    
    return '';
  };

  // Validation function for CurrentMetrics step
  const validateCurrentMetrics = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Check scheduling methods
    if (!formData.schedulingMethods || formData.schedulingMethods.length === 0) {
      errors.push("Please select at least one scheduling method");
    }

    // Check if Calculate Values was used
    if (!calculationBasis) {
      errors.push("Please click 'Calculate Values' in the Compensation & Vacancy Costs section to set salary and vacancy costs");
    }

    // Check required numeric fields
    const requiredFields = [
      { field: 'avgConfirmationTime', label: 'Average Time to Confirm Interview' },
      { field: 'adminTimePerHire', label: 'Admin Time per Hire' },
      { field: 'candidateSatisfaction', label: 'Candidate Satisfaction' },
      { field: 'interviewerSatisfaction', label: 'Interviewer Satisfaction' },
      { field: 'reschedulingRate', label: 'Rescheduling Rate' },
      { field: 'interviewerAvailability', label: 'Interviewer Availability' },
      { field: 'interviewNoShowRate', label: 'Interview No-Show Rate' },
      { field: 'timeToFill', label: 'Time to Fill' },
      { field: 'costPerHire', label: 'Cost per Hire' },
      { field: 'avgSalary', label: 'Average Salary' },
      { field: 'dailyVacancyCost', label: 'Daily Vacancy Cost' }
    ];
    
    requiredFields.forEach(({ field, label }) => {
      const value = formData[field as keyof FormData];
      if (value === '' || value === 0 || value === undefined || value === null) {
        errors.push(`${label} is required`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Unified ROI calculation function that includes all factors
  const calculateCompleteROI = useCallback((overrides?: {
    tasImprovement?: number;
    annualHires?: number;
    scenarioMode?: 'conservative' | 'moderate' | 'optimistic';
    implementationInvestment?: ImplementationInvestment;
    timeToValue?: TimeToValueInputs;
    attributionFactors?: AttributionFactors;
  }) => {
    // Recalculate TAS to ensure we have the latest value based on current form data
    const { score: currentTAS, components } = calculateTAS();
    
    // Use explicitly passed values or fall back to form/component state
    const tasImprovementValue = overrides?.tasImprovement ?? getNumericValue(formData.tasImprovement, 20);
    const annualHiresValue = overrides?.annualHires ?? getNumericValue(formData.annualHires);
    const currentScenarioMode = overrides?.scenarioMode ?? scenarioMode;
    const currentImplementationInvestment = overrides?.implementationInvestment ?? implementationInvestment;
    const currentTimeToValue = overrides?.timeToValue ?? timeToValue;
    const currentAttributionFactors = overrides?.attributionFactors ?? attributionFactors;
    
    const targetTAS = currentTAS + tasImprovementValue;
    // Pass scenario mode and form data to calculateROI to avoid stale closure issues
    const roiResults = calculateROI(currentTAS, targetTAS, currentScenarioMode, formData);
    
    // Calculate subscription cost using unified tiered pricing
    const estimatedAnnualSubscription = getAnnualSubscriptionCost(annualHiresValue);
    
    // Calculate implementation investment (scales with timeline)
    const implInvestment = calculateImplementationInvestment(
      currentImplementationInvestment,
      estimatedAnnualSubscription,
      currentTimeToValue.implementationWeeks
    );
    
    // Calculate Year 1 and steady-state savings
    const year1Savings = calculateYear1Savings(
      roiResults.savings,
      currentTimeToValue,
      currentAttributionFactors
    );
    
    const steadyStateSavings = calculateSteadyStateSavings(
      roiResults.savings,
      currentAttributionFactors
    );
    
    // Generate monthly projections
    const monthlyProjections = generateMonthlyProjections(
      roiResults.savings,
      currentTimeToValue,
      currentAttributionFactors,
      implInvestment,
      estimatedAnnualSubscription
    );
    
    // Calculate payback metrics
    // Note: Only pass implementation investment as totalInvestment parameter
    // The subscription cost is already included in the monthly projections
    const paybackMetrics = calculatePaybackPeriod(
      monthlyProjections,
      implInvestment
    );
    
    // Calculate Year 1 and steady-state ROI (actual calculations, no artificial minimums)
    const year1TotalSavings = year1Savings.timeToFillSavings + 
      year1Savings.costPerHireSavings + 
      year1Savings.productivityGain + 
      year1Savings.schedulingTimeSavings + 
      year1Savings.noShowReductionSavings;
    
    const year1Investment = implInvestment + estimatedAnnualSubscription;
    
    // Calculate actual ROI
    const year1ROI = year1Investment > 0 ? year1TotalSavings / year1Investment : 0;

    const steadyStateTotalSavings = steadyStateSavings.totalAnnualSavings;
    const steadyStateROI = estimatedAnnualSubscription > 0 ?
      steadyStateTotalSavings / estimatedAnnualSubscription : 0;

    const updatedResults = {
      ...results,
      currentTAS,  // Update with recalculated TAS
      projectedTAS: targetTAS,
      tasComponents: components,  // Update with recalculated components
      metrics: {
        current: roiResults.currentMetrics,
        improved: roiResults.improvedMetrics
      },
      savings: roiResults.savings,
      roi: roiResults.roi,
      annualRoi: roiResults.annualRoi,
      // Add enhanced time-to-value results
      enhancedSavings: {
        ...roiResults.savings,
        year1Savings: {
          ...year1Savings,
          totalAnnualSavings: year1TotalSavings
        },
        steadyStateSavings
      },
      year1ROI,
      steadyStateROI,
      paybackMetrics,
      monthlyProjections,
      implementationInvestment: implInvestment
    };
    
    setResults(updatedResults);
    
    // Auto-save ROI calculations if we're on Step 4
    if (currentStep === CalculatorStep.ROIAnalysis && sessionId) {
      // Merge any overrides into formData to ensure we save what the user sees
      const saveFormData = {
        ...formData,
        ...(overrides?.tasImprovement !== undefined && { tasImprovement: tasImprovementValue }),
        ...(overrides?.annualHires !== undefined && { annualHires: annualHiresValue })
      };
      
      debouncedROISave(sessionId, saveFormData, updatedResults);
    }
    
    return {
      year1ROI,
      steadyStateROI,
      paybackMetrics,
      year1TotalSavings,
      steadyStateTotalSavings
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, results.currentTAS, timeToValue, attributionFactors, implementationInvestment, scenarioMode]);

  
  // Reactive update when implementation assumptions change (only on ROI page)
  useEffect(() => {
    if (currentStep === CalculatorStep.ROIAnalysis && results.currentTAS > 0) {
      calculateCompleteROI();
      
      // Save ROI data immediately on first arrival at Step 4
      if (sessionId && formData.contactEmail && !hasInitialROISave.current) {
        // Small delay to let the state update from calculateCompleteROI
        setTimeout(() => {
          if (results.enhancedSavings || results.year1ROI) {
            saveCheckpoint(sessionId, CalculatorStep.ROIAnalysis, formData, results, leadSource, editingReport, originalReportId || undefined);
            hasInitialROISave.current = true;
          }
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, scenarioMode, timeToValue.implementationWeeks, timeToValue.adoptionRampMonths]);

  // Reset report finalization when critical form data changes
  // This ensures the finalized report always matches the current calculations
  useEffect(() => {
    // Only reset if we have a finalized report and we're not on ROI page
    // (ROI page changes are handled separately by their own handlers)
    if (reportFinalized && currentStep !== CalculatorStep.ROIAnalysis) {
      setReportFinalized(false);
      clearPDFAndReportWindow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Watch all fields that affect ROI calculations
    formData.annualHires,
    formData.avgConfirmationTime,
    formData.adminTimePerHire,
    formData.candidateSatisfaction,
    formData.interviewerSatisfaction,
    formData.reschedulingRate,
    formData.interviewerAvailability,
    formData.interviewNoShowRate,
    formData.timeToFill,
    formData.costPerHire,
    formData.dailyVacancyCost,
    formData.avgSalary
  ]);

  // Manual report finalization function
  const finalizeReport = async () => {
    // Validate that we have an email
    if (!formData.contactEmail) {
      alert('Please provide your email address in Step 1 to finalize the report.');
      return;
    }

    // Save to Firestore
    if (isFirebaseConfigured() && (results.savings?.totalAnnualSavings || 0) > 0) {
      // Save final checkpoint - ensure we save at Step 4 (ROI Analysis) before marking complete
      if (sessionId) {
        // First, ensure we're at the correct step and update highestStepReached
        if (currentStep < CalculatorStep.ROIAnalysis) {
          setCurrentStep(CalculatorStep.ROIAnalysis);
          setHighestStepReached(CalculatorStep.ROIAnalysis);
        }

        // Save checkpoint with the correct step (ROI Analysis = Step 4)
        const checkpointSaved = await saveCheckpoint(sessionId, CalculatorStep.ROIAnalysis, formData, results, leadSource, editingReport, originalReportId || undefined);

        // Only mark as completed if checkpoint was successfully saved
        if (checkpointSaved) {
          await markSessionCompleted(sessionId, CalculatorStep.ROIAnalysis, formData.contactEmail);
        } else {
          console.warn('Failed to save checkpoint before marking session completed');
        }
      }
      
      // Prepare form data for Firestore service with all time-to-value settings
      const firestoreFormData = {
        ...formData,
        timeToValue,
        attributionFactors,
        implementationInvestment,
        scenarioMode,
        pilotMode
      };
      
      // Enhance results with sessionId for tracking
      const enhancedResults = {
        ...results,
        sessionId
      };
      
      // Calculate annual subscription for saving
      const annualSub = getAnnualSubscriptionCost(getNumericValue(formData.annualHires));
      const implInvestment = implementationInvestment.mode === 'custom' 
        ? implementationInvestment.customAmount 
        : calculateImplementationInvestment(implementationInvestment, annualSub, timeToValue.implementationWeeks);
      
      // Save to user-centric structure with existing sessionId
      setSessionSaving(true);
      setSessionSaved(false);
      
      try {
        // Recalculate TAS to ensure we have the fresh value
        const { score: freshCurrentTAS, components: freshComponents } = calculateTAS();
        const freshProjectedTAS = freshCurrentTAS + getNumericValue(formData.tasImprovement, 20);
        
        // Calculate ROI drivers for the report
        const recommendedFeatures = getRecommendedFeatures(freshComponents, 8);
        const roiDrivers = recommendedFeatures.map(feature => {
          const totalImpact = calculateFeatureTotalImpact(feature);
          const roiContribution = (totalImpact / 100) * 0.15;
          const estimatedSavings = (results.savings?.totalAnnualSavings || 0) * roiContribution;
          
          return {
            ...feature,
            estimatedSavings,
            roiPercentage: Math.round(roiContribution * 100)
          };
        });

        // Prepare enhanced data for the PDF
        const enhancedData = {
          scenarioMode,
          annualSubscription: annualSub,
          implementationInvestment: implInvestment,
          paybackPeriod: results.paybackMetrics?.paybackMonth || 0,
          year1ROI: results.year1ROI || 0,
          steadyStateROI: results.steadyStateROI || 0,
          enhancedSavings: results.enhancedSavings,
          topPerformerTAS: calculateTopPerformerTAS(formData.industry),
          industryAverageTAS: calculateIndustryTAS(formData.industry),
          // Include all calculation data for single source of truth
          componentData: getComponentData(),
          improvementData: getImprovementData(),
          tasComponents: freshComponents,
          currentTAS: freshCurrentTAS,
          projectedTAS: freshProjectedTAS,
          roiDrivers: roiDrivers.slice(0, 8)
        };

        // Save the finalized report data to Firestore with ALL form fields
        const userId = await createUserDocumentId(formData.contactEmail);
        
        // Ensure we save the complete formData with all fields exactly as they are
        const completeFormData: CalculatorFormData = {
          organizationName: formData.organizationName,
          contactName: formData.contactName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          contactTitle: formData.contactTitle,
          industry: formData.industry,
          annualHires: formData.annualHires,
          fteCount: inputMode === 'fte' ? fteCount : undefined,  // Save FTE if user entered FTE
          inputMode: inputMode,  // Save which input mode was used
          schedulingMethods: formData.schedulingMethods,  // Include scheduling methods
          avgConfirmationTime: formData.avgConfirmationTime,
          adminTimePerHire: formData.adminTimePerHire,
          candidateSatisfaction: formData.candidateSatisfaction,
          interviewerSatisfaction: formData.interviewerSatisfaction,
          reschedulingRate: formData.reschedulingRate,
          interviewerAvailability: formData.interviewerAvailability,
          interviewNoShowRate: formData.interviewNoShowRate,
          timeToFill: formData.timeToFill,
          costPerHire: formData.costPerHire,
          dailyVacancyCost: formData.dailyVacancyCost,
          avgSalary: formData.avgSalary,
          tasImprovement: formData.tasImprovement,
          timeToValue: timeToValue,
          attributionFactors: attributionFactors,
          implementationInvestment: implementationInvestment,
          scenarioMode: scenarioMode,
          pilotMode: pilotMode,
          currency: formData.currency || 'USD'
        };
        
        const reportData = {
          // Critical fields for admin panel and routing
          hasROIAnalysis: true, // This is an ROI report (has ROI analysis step)
          reportType: 'ROI_ANALYSIS' as const,
          organizationName: formData.organizationName || 'Unknown Organization',
          formData: completeFormData,
          results,
          componentData: getComponentData(),
          improvementData: getImprovementData(),
          enhancedData: {
            scenarioMode,
            annualSubscription: annualSub,
            implementationInvestment: implInvestment || 0,
            paybackPeriod: results.paybackMetrics?.paybackMonth || 0,
            year1ROI: results.year1ROI || 0,
            steadyStateROI: results.steadyStateROI || 0,
            enhancedSavings: results.enhancedSavings || {},
            topPerformerTAS: calculateTopPerformerTAS(formData.industry),
            industryAverageTAS: calculateIndustryTAS(formData.industry)
          },
          leadSource
        };
        
        const reportId = await saveFinalizedReport(userId, sessionId, reportData);
        
        // Store the report ID in state
        setCurrentReportId(reportId);
        
        // Store metadata for report retrieval
        storeReportMetadata(reportId, userId, sessionId);
        
        // Open the HTML report with the report ID
        await generatePDFReport(completeFormData, results, getComponentData, getImprovementData, enhancedData, sessionId, reportId);
        
        // Save session data (without PDF URL since we're not generating PDFs)
        const sessionData = await saveUserCalculatorSession(
          formData.contactEmail,
          sessionId,
          firestoreFormData,
          enhancedResults,
          enhancedData,
          leadSource
        );
        
        if (sessionData) {
          setSessionSaved(true);
        } else {
        }
        
        // Always finalize the report, regardless of save success
        setReportFinalized(true);
      } catch (error) {
        console.error('Error saving user session:', error);
        // Still allow finalization even if save fails
        setReportFinalized(true);
        
        // Report is ready even if save fails
        const reportWindow = (window as any).reportWindow;
        if (reportWindow && !reportWindow.closed) {
        }
      } finally {
        setSessionSaving(false);
      }
    } else {
      // If Firebase isn't configured or no valid results, still finalize
      setReportFinalized(true);
      
      const reportWindow = (window as any).reportWindow;
      if (reportWindow && !reportWindow.closed) {
      }
    }
  };

  // Safe percentage helper to prevent NaN/undefined CSS crashes
  const safePercentage = (value: number | undefined | null): number => {
    const num = Number(value);
    if (!isFinite(num) || isNaN(num)) return 0;
    return Math.min(100, Math.max(0, num));
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Helper function to transform component data for chart
  const getComponentData = () => {
    if (!results.tasComponents) return [];
    
    return [
      {
        name: "Scheduling Velocity (SV)",
        value: Math.round(results.tasComponents.schedulingVelocity),
        weight: "30%",
        color: "#0284c7", // indigo - consistent brand color
        description: "Speed of coordinating and confirming interviews",
        calculation: "Based on average time to confirm interviews (lower is better)",
        importance: "Represents how quickly your team can arrange interviews, directly impacting time-to-fill",
        formula: "(1 - (avgConfirmationTime / 120 hours)) × 100, min 5%"
      },
      {
        name: "Scheduling Efficiency (SE)",
        value: Math.round(results.tasComponents.schedulingEfficiency),
        weight: "25%",
        color: "#0284c7", // indigo - consistent brand color
        description: "Administrative efficiency in scheduling process",
        calculation: "Based on time spent per hire on scheduling tasks",
        importance: "Measures resource utilization and cost-effectiveness of your scheduling process",
        formula: "(1 - (adminTimePerHire / 360 minutes)) × 100, min 5%"
      },
      {
        name: "Candidate Experience (CE)",
        value: Math.round(results.tasComponents.candidateExperience),
        weight: "20%",
        color: "#0284c7", // indigo - consistent brand color
        description: "Quality of candidate scheduling experience",
        calculation: "Directly tied to candidate satisfaction ratings",
        importance: "Critical for employer branding and acceptance rates",
        formula: "Candidate satisfaction percentage (direct value)"
      },
      {
        name: "Scheduling Integrity (SI)",
        value: Math.round(results.tasComponents.schedulingIntegrity),
        weight: "15%",
        color: "#0284c7", // indigo - consistent brand color
        description: "Dependability of the interview scheduling process",
        calculation: "Based on rescheduling rates (67%) and no-show rates (33%)",
        importance: "Measures consistency and reliability of your scheduling system",
        formula: "((1 - (reschedulingRate / 30%)) × 67%) + ((1 - (noShowRate / 30%)) × 33%)"
      },
      {
        name: "Scheduling Utilization (SU)",
        value: Math.round(results.tasComponents.schedulingUtilization),
        weight: "10%",
        color: "#0284c7", // indigo - consistent brand color
        description: "Effective use of interviewer time and availability",
        calculation: "Based on interviewer satisfaction and calendar availability",
        importance: "Measures how well you optimize interviewer resources and time",
        formula: "(interviewerAvailability + interviewerSatisfaction) / 2"
      }
    ];
  };
  
  // Calculate industry average TAS from actual metrics (mathematically defensible)
  const calculateIndustryTAS = (industryName: string): number => {
    const defaults = INDUSTRY_DEFAULTS[industryName as IndustryName];
    if (!defaults) return 50;
    
    // Calculate components using same formulas as user calculations
    const schedulingVelocity = Math.max(5, (1 - defaults.avgConfirmationTime / 120) * 100);
    const schedulingEfficiency = Math.max(5, (1 - defaults.adminTimePerHire / 360) * 100);
    const candidateExperience = defaults.candidateSatisfaction * 10;
    const rescheduleReliability = Math.max(0, (1 - defaults.reschedulingRate / 30) * 100);
    const noShowReliability = Math.max(0, (1 - defaults.interviewNoShowRate / 30) * 100);
    const schedulingIntegrity = (rescheduleReliability * 2/3) + (noShowReliability * 1/3);
    const schedulingUtilization = (defaults.interviewerAvailability * 10 + defaults.interviewerSatisfaction * 10) / 2;
    
    // Calculate raw score with same weights
    const rawScore = (
      (schedulingVelocity * 0.30) +
      (schedulingEfficiency * 0.25) +
      (candidateExperience * 0.20) +
      (schedulingIntegrity * 0.15) +
      (schedulingUtilization * 0.10)
    );
    
    // Apply same curve adjustment as user calculations
    let adjustedScore;
    if (rawScore < 50) {
      adjustedScore = rawScore * 0.9;
    } else if (rawScore < 75) {
      const reductionFactor = 0.9 - ((rawScore - 50) / 25) * 0.15;
      adjustedScore = rawScore * reductionFactor;
    } else {
      adjustedScore = 60 + (rawScore - 75) * 0.8;
    }
    
    // NO scheduling method multiplier - the metrics already reflect real-world performance
    // with whatever scheduling methods are typically used in that industry
    return Math.round(adjustedScore);
  };

  // Calculate top performer TAS from improved metrics (mathematically defensible)
  const calculateTopPerformerTAS = (industryName: string): number => {
    const defaults = INDUSTRY_DEFAULTS[industryName as IndustryName];
    if (!defaults) return 70;
    
    // Define top performer metrics based on spec guidance and reasonable improvements
    // Spec says "Top performers confirm <24 hours"
    const topPerformerMetrics = {
      avgConfirmationTime: 24,  // <24 hours as per spec
      adminTimePerHire: defaults.adminTimePerHire * 0.5,  // 50% less admin time
      candidateSatisfaction: 8,  // High satisfaction (8/10)
      reschedulingRate: defaults.reschedulingRate * 0.5,  // 50% fewer reschedules
      interviewNoShowRate: defaults.interviewNoShowRate * 0.5,  // 50% fewer no-shows
      interviewerSatisfaction: 8,  // High satisfaction
      interviewerAvailability: 8   // High availability
    };
    
    // Calculate components using same formulas as user calculations
    const schedulingVelocity = Math.max(5, (1 - topPerformerMetrics.avgConfirmationTime / 120) * 100);
    const schedulingEfficiency = Math.max(5, (1 - topPerformerMetrics.adminTimePerHire / 360) * 100);
    const candidateExperience = topPerformerMetrics.candidateSatisfaction * 10;
    const rescheduleReliability = Math.max(0, (1 - topPerformerMetrics.reschedulingRate / 30) * 100);
    const noShowReliability = Math.max(0, (1 - topPerformerMetrics.interviewNoShowRate / 30) * 100);
    const schedulingIntegrity = (rescheduleReliability * 2/3) + (noShowReliability * 1/3);
    const schedulingUtilization = (topPerformerMetrics.interviewerAvailability * 10 + topPerformerMetrics.interviewerSatisfaction * 10) / 2;
    
    // Calculate raw score with same weights
    const rawScore = (
      (schedulingVelocity * 0.30) +
      (schedulingEfficiency * 0.25) +
      (candidateExperience * 0.20) +
      (schedulingIntegrity * 0.15) +
      (schedulingUtilization * 0.10)
    );
    
    // Apply same curve adjustment as user calculations
    let adjustedScore;
    if (rawScore < 50) {
      adjustedScore = rawScore * 0.9;
    } else if (rawScore < 75) {
      const reductionFactor = 0.9 - ((rawScore - 50) / 25) * 0.15;
      adjustedScore = rawScore * reductionFactor;
    } else {
      adjustedScore = 60 + (rawScore - 75) * 0.8;
    }
    
    // NO scheduling method multiplier - top performers have optimized processes
    return Math.round(adjustedScore);
  };

  
  // Helper function to format metrics data for the ROI analysis chart
  const getImprovementData = () => {
    if (!results.metrics || !results.metrics.current || !results.metrics.improved) {
      return [];
    }
    
    // Calculate the no-show rate improvement
    const interviewNoShowRateValue = getNumericValue(formData.interviewNoShowRate);
    const tasImprovementValue = getNumericValue(formData.tasImprovement, 20);
    const currentNoShowRate = Math.round(interviewNoShowRateValue); // Round to whole percent
    const noShowRateReduction = Math.min(0.60 * (tasImprovementValue / 30), 0.75);
    const improvedNoShowRate = Math.round(currentNoShowRate * (1 - noShowRateReduction)); // Round to whole percent
    
    return [
      {
        name: "TAS Score",
        before: results.currentTAS,
        after: results.projectedTAS,
        unit: "", // no specific unit
        improvement: ((results.projectedTAS - results.currentTAS) / results.currentTAS * 100).toFixed(0),
        higherIsBetter: true
      },
      {
        name: "Time to Fill",
        before: Math.round(results.metrics.current.timeToFill), // Round to whole days
        after: Math.round(results.metrics.improved.timeToFill), // Round to whole days
        unit: "days",
        improvement: ((results.metrics.current.timeToFill - results.metrics.improved.timeToFill) / results.metrics.current.timeToFill * 100).toFixed(0),
        higherIsBetter: false
      },
      {
        name: "Cost per Hire",
        before: results.metrics.current.costPerHire,
        after: results.metrics.improved.costPerHire,
        unit: "$",
        improvement: ((results.metrics.current.costPerHire - results.metrics.improved.costPerHire) / results.metrics.current.costPerHire * 100).toFixed(0),
        higherIsBetter: false
      },
      {
        name: "Vacancy Cost",
        before: results.metrics.current.vacancyCost,
        after: results.metrics.improved.vacancyCost,
        unit: "$",
        improvement: ((results.metrics.current.vacancyCost - results.metrics.improved.vacancyCost) / results.metrics.current.vacancyCost * 100).toFixed(0),
        higherIsBetter: false
      },
      {
        name: "No-Show Rate",
        before: currentNoShowRate,
        after: improvedNoShowRate,
        unit: "%",
        improvement: ((currentNoShowRate - improvedNoShowRate) / currentNoShowRate * 100).toFixed(0),
        higherIsBetter: false
      }
    ];
  };

  // Navigation functions with checkpoint saves
  const goToStep = async (step: CalculatorStep) => {
    // Calculate time spent on current step
    const timeOnStep = Math.round((Date.now() - stepStartTime) / 1000); // in seconds

    // Determine if we're moving forward or backward
    const isMovingForward = step > currentStep;

    // Update highestStepReached only when moving forward
    if (isMovingForward && step > highestStepReached) {
      setHighestStepReached(step);
    }

    // Update analytics for the step we're ENTERING (not leaving)
    if (sessionId) {
      await updateAnalyticsSession(sessionId, step, {
        industry: formData.industry,
        companySize: formData.annualHires ? String(formData.annualHires) : undefined,
        timeOnStep
      });

      // Only save checkpoint when moving forward to prevent overwriting progress
      if (isMovingForward) {
        await saveCheckpoint(sessionId, step, formData, results.currentTAS > 0 ? results : undefined, leadSource, editingReport, originalReportId || undefined);
      }
    }

    // Reset report finalization if going back from ROI Analysis
    // This ensures users can re-finalize after making changes to earlier steps
    if (currentStep === CalculatorStep.ROIAnalysis && reportFinalized && step < CalculatorStep.ROIAnalysis) {
      setReportFinalized(false);
      clearPDFAndReportWindow();
    }

    // Reset the initial ROI save flag if leaving Step 4
    if (currentStep === CalculatorStep.ROIAnalysis && step !== CalculatorStep.ROIAnalysis) {
      hasInitialROISave.current = false;
    }

    // When moving to CurrentMetrics (Step 2) for the first time, clear touched fields
    // This allows defaults to be applied fresh
    if (step === CalculatorStep.CurrentMetrics && currentStep < step) {
      setTouchedFields(new Set());
      // The useEffect will automatically apply adjustedDefaults to untouched fields
    }

    setCurrentStep(step);
    setStepStartTime(Date.now()); // Reset timer for new step
    // Scroll to the top of the page when changing steps (with immediate jump)
    window.scrollTo(0, 0);
  };
  
  // Handle for viewing the report - simply reopen the HTML report tab
  const downloadPDFReport = async () => {
    
    // Make sure report is finalized first
    if (!reportFinalized) {
      alert('Please click "Finalize Report" before viewing the report');
      return;
    }
    
    setIsGeneratingReport(true);
    
    try {
      // Calculate the annual subscription cost for the report
      const annualSubscription = getAnnualSubscriptionCost(getNumericValue(formData.annualHires));
    
      // Get implementation investment amount
      const implInvestment = implementationInvestment.mode === 'custom' 
        ? implementationInvestment.customAmount 
        : calculateImplementationInvestment(implementationInvestment, annualSubscription, timeToValue.implementationWeeks);
      
      // Recalculate TAS to ensure we have the fresh value
      const { score: freshCurrentTAS, components: freshComponents } = calculateTAS();
      const freshProjectedTAS = freshCurrentTAS + getNumericValue(formData.tasImprovement, 20);
      
      // Calculate ROI drivers for the report
      const recommendedFeatures = getRecommendedFeatures(freshComponents, 8);
      const roiDrivers = recommendedFeatures.map(feature => {
        const totalImpact = calculateFeatureTotalImpact(feature);
        const roiContribution = (totalImpact / 100) * 0.15;
        const estimatedSavings = (results.savings?.totalAnnualSavings || 0) * roiContribution;
        
        return {
          ...feature,
          estimatedSavings,
          roiPercentage: Math.round(roiContribution * 100)
        };
      });

      // Prepare enhanced data for the report
      const enhancedData = {
        scenarioMode,
        annualSubscription,
        implementationInvestment: implInvestment,
        paybackPeriod: results.paybackMetrics?.paybackMonth || 0,
        year1ROI: results.year1ROI || 0,
        steadyStateROI: results.steadyStateROI || 0,
        enhancedSavings: results.enhancedSavings,
        topPerformerTAS: calculateTopPerformerTAS(formData.industry),
        industryAverageTAS: calculateIndustryTAS(formData.industry),
        componentData: getComponentData(),
        improvementData: getImprovementData(),
        tasComponents: freshComponents,
        currentTAS: freshCurrentTAS,
        projectedTAS: freshProjectedTAS,
        roiDrivers: roiDrivers.slice(0, 8)
      };
      
      // Open the HTML report in a new tab (users can print/save as PDF from browser)
      // Include inputMode and fteCount in the formData for proper display
      const completeFormData = {
        ...formData,
        inputMode: inputMode,
        fteCount: inputMode === 'fte' ? fteCount : undefined
      };
      await generatePDFReport(completeFormData, results, getComponentData, getImprovementData, enhancedData, sessionId, currentReportId || undefined);
    } finally {
      // Always clear loading state
      setIsGeneratingReport(false);
    }
  };

  // Handle for viewing the TAS report - reopen the TAS HTML report
  const downloadTasReport = async () => {
    // Make sure report is finalized first
    if (!reportFinalized) {
      alert('Please click "Finalize Report" before viewing the report');
      return;
    }
    
    setIsGeneratingReport(true);
    
    try {
      // Recalculate TAS to ensure we have the fresh value
      const { score: freshCurrentTAS } = calculateTAS();
      const freshProjectedTAS = freshCurrentTAS + getNumericValue(formData.tasImprovement, 20);

      // Prepare enhanced data for the TAS report
      const enhancedData = {
        scenarioMode: 'moderate' as const,
        annualSubscription: 0,
        implementationInvestment: 0,
        paybackPeriod: 0,
        year1ROI: 0,
        steadyStateROI: 0,
        enhancedSavings: {},
        topPerformerTAS: calculateTopPerformerTAS(formData.industry),
        industryAverageTAS: calculateIndustryTAS(formData.industry),
        componentData: getComponentData(),
        currentTAS: freshCurrentTAS,
        projectedTAS: freshProjectedTAS
      };
      
      // Open the TAS HTML report in a new tab
      // Include inputMode and fteCount in the formData for proper display
      const completeFormData = {
        ...formData,
        inputMode: inputMode,
        fteCount: inputMode === 'fte' ? fteCount : undefined
      };
      await generateTasPDFReport(completeFormData, results, getComponentData, isRoosterBranded, enhancedData, sessionId, currentReportId || undefined);
    } finally {
      // Always clear loading state
      setIsGeneratingReport(false);
    }
  };

  // Finalize TAS report function for /tas and /rooster-tas routes (similar to finalizeReport)
  const finalizeTasReport = async () => {
    // Validate that we have an email
    if (!formData.contactEmail) {
      alert('Please provide your email address in Step 1 to finalize the report.');
      return;
    }

    // Save to Firestore
    if (isFirebaseConfigured() && results.currentTAS) {
      // Save final checkpoint - ensure we save at Step 3 (TAS Score) before marking complete
      if (sessionId) {
        // First, ensure we're at the correct step and update highestStepReached
        if (currentStep < CalculatorStep.TasScore) {
          setCurrentStep(CalculatorStep.TasScore);
          setHighestStepReached(CalculatorStep.TasScore);
        }

        // Save checkpoint with the correct step (TAS Score = Step 3)
        const checkpointSaved = await saveCheckpoint(sessionId, CalculatorStep.TasScore, formData, results, leadSource, editingReport, originalReportId || undefined);

        // Only mark as completed if checkpoint was successfully saved
        if (checkpointSaved) {
          await markSessionCompleted(sessionId, CalculatorStep.TasScore, formData.contactEmail);
        } else {
          console.warn('Failed to save checkpoint before marking session completed');
        }
      }
      
      // Set saving state
      setSessionSaving(true);
      setSessionSaved(false);
      
      try {
        // Recalculate TAS to ensure we have the fresh value
        const { score: freshCurrentTAS, components: freshComponents } = calculateTAS();
        const freshProjectedTAS = freshCurrentTAS + getNumericValue(formData.tasImprovement, 20);

        // Prepare enhanced data for the TAS report
        const enhancedData = {
          scenarioMode: 'moderate' as const,
          annualSubscription: 0,
          implementationInvestment: 0,
          paybackPeriod: 0,
          year1ROI: 0,
          steadyStateROI: 0,
          enhancedSavings: {},
          topPerformerTAS: calculateTopPerformerTAS(formData.industry),
          industryAverageTAS: calculateIndustryTAS(formData.industry),
          componentData: getComponentData(),
          currentTAS: freshCurrentTAS,
          projectedTAS: freshProjectedTAS
        };

        // Create user document ID
        const userId = await createUserDocumentId(formData.contactEmail);
        
        // Prepare complete form data for saving
        const completeFormData: CalculatorFormData = {
          organizationName: formData.organizationName,
          contactName: formData.contactName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          contactTitle: formData.contactTitle,
          industry: formData.industry,
          annualHires: formData.annualHires,
          fteCount: inputMode === 'fte' ? fteCount : undefined,
          inputMode: inputMode,
          schedulingMethods: formData.schedulingMethods,
          avgConfirmationTime: formData.avgConfirmationTime,
          adminTimePerHire: formData.adminTimePerHire,
          candidateSatisfaction: formData.candidateSatisfaction,
          interviewerSatisfaction: formData.interviewerSatisfaction,
          reschedulingRate: formData.reschedulingRate,
          interviewerAvailability: formData.interviewerAvailability,
          interviewNoShowRate: formData.interviewNoShowRate,
          timeToFill: formData.timeToFill,
          costPerHire: formData.costPerHire,
          dailyVacancyCost: formData.dailyVacancyCost,
          avgSalary: formData.avgSalary,
          tasImprovement: formData.tasImprovement,
          currency: formData.currency || 'USD',
          calculatorPath: formData.calculatorPath || window.location.pathname
        };
        
        // Generate component data directly from fresh components
        const componentData = freshComponents ? Object.entries(freshComponents).map(([key, value]) => {
          const componentNames = {
            schedulingVelocity: "Scheduling Velocity (SV)",
            schedulingEfficiency: "Scheduling Efficiency (SE)", 
            candidateExperience: "Candidate Experience (CE)",
            schedulingIntegrity: "Scheduling Integrity (SI)",
            schedulingUtilization: "Scheduling Utilization (SU)"
          };
          return {
            name: componentNames[key as keyof typeof componentNames] || key,
            value: Math.round(value),
            color: '#0284c7'
          };
        }) : [];
        
        // Prepare TAS report data for saving
        const reportData = {
          // Critical fields for admin panel and routing
          hasROIAnalysis: false, // This is a TAS-only report (no ROI analysis)
          reportType: 'TAS_ONLY' as const,
          organizationName: formData.organizationName || 'Unknown Organization',
          formData: completeFormData,
          results: {...results, currentTAS: freshCurrentTAS, projectedTAS: freshProjectedTAS, tasComponents: freshComponents},
          componentData: componentData,
          improvementData: [],
          enhancedData: enhancedData,
          leadSource
        };
        
        const reportId = await saveFinalizedReport(userId, sessionId, reportData);
        
        // Store the report ID in state
        setCurrentReportId(reportId);
        
        // Store metadata for report retrieval
        storeReportMetadata(reportId, userId, sessionId);
        
        // Open the TAS report with the report ID
        await generateTasPDFReport(completeFormData, results, getComponentData, isRoosterBranded, enhancedData, sessionId, reportId);
        
        // Save session data
        const sessionData = await saveUserCalculatorSession(
          formData.contactEmail,
          sessionId,
          completeFormData,
          {...results, currentTAS: freshCurrentTAS, projectedTAS: freshProjectedTAS, tasComponents: freshComponents},
          enhancedData,
          leadSource
        );
        
        if (sessionData) {
          setSessionSaved(true);
        }
        
        // Always finalize the report, regardless of save success
        setReportFinalized(true);
      } catch (error) {
        console.error('Error saving TAS report:', error);
        // Still allow finalization even if save fails
        setReportFinalized(true);
      } finally {
        setSessionSaving(false);
      }
    } else {
      // If Firebase isn't configured or no valid results, still finalize
      setReportFinalized(true);
    }
  };

  // Handle for editing the current report - enter edit mode
  const enterEditMode = () => {
    // Set editing flags
    setEditingReport(true);
    setOriginalReportId(currentReportId);

    // Clear finalization to enable editing
    setReportFinalized(false);
    clearPDFAndReportWindow();

    // Navigate to step 1 for editing but preserve highestStepReached
    // No need to reset highestStepReached since user already completed these steps
    goToStep(CalculatorStep.OrganizationDetails);

    // Scroll to top for clean UX
    window.scrollTo(0, 0);
  };

  // Progress bar steps - filter based on version
  const allSteps = [
    { id: CalculatorStep.OrganizationDetails, name: "Organization Details" },
    { id: CalculatorStep.CurrentMetrics, name: "Scheduling Performance" },
    { id: CalculatorStep.TasScore, name: "TAS Score" },
    { id: CalculatorStep.ROIAnalysis, name: "ROI Analysis" }
  ];
  
  const steps = hasROIAnalysis 
    ? allSteps 
    : allSteps.slice(0, 3); // Only show first 3 steps for non-ROI versions
  
  // Auto-expand lowest scoring component when entering TAS Score step
  useEffect(() => {
    if (currentStep === CalculatorStep.TasScore && !autoExpandComplete && results && results.tasComponents) {
      // Find the lowest scoring component
      const components = Object.entries(results.tasComponents);
      const lowestComponent = components.sort(([, a], [, b]) => a - b)[0];
      
      if (lowestComponent) {
        const [componentKey] = lowestComponent;
        // Auto-expand the lowest scoring component with a delay so users notice it
        setTimeout(() => {
          setExpandedComponents(new Set([componentKey]));
          setAutoExpandComplete(true);
        }, 1000); // 1 second delay for users to see the expansion happen
      }
    }
  }, [currentStep, autoExpandComplete, results]);
  
  // Handle scroll effect for the sticky header
  useEffect(() => {
    const handleScroll = () => {
      const progressBar = document.querySelector('.progress-bar-sticky');
      if (progressBar) {
        if (window.scrollY > 10) {
          progressBar.classList.add('scrolled');
        } else {
          progressBar.classList.remove('scrolled');
        }
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <div className="max-w-4xl mx-auto">
      <style>{customStyles}</style>
      {/* Clean Progress Bar with Circles */}
      <div className="progress-bar-sticky">
        <div className="max-w-3xl mx-auto px-2 sm:px-4 py-2">
          {/* Progress steps */}
          <div className="relative z-10">
            <div className={`grid ${steps.length === 3 ? 'grid-cols-3' : 'grid-cols-4'} gap-2 sm:gap-4`}>
              {steps.map((step, index) => {
                const isCompleted = step.id < currentStep;
                const isCurrent = step.id === currentStep;
                const isClickable = step.id <= highestStepReached;
                
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => isClickable ? goToStep(step.id) : null}
                    disabled={!isClickable}
                    className={`
                      flex flex-col items-center justify-center
                      ${isClickable ? 'cursor-pointer hover:scale-105 transition-transform' : 'cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded-lg p-1
                    `}
                  >
                    {/* Step circle */}
                    <div 
                      className={`
                        w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border-2 relative z-10
                        ${isCompleted ? 'bg-sky-600 text-white border-sky-600' : 
                          isCurrent ? 'bg-white text-sky-600 border-sky-600 ring-2 ring-sky-100' : 
                          'bg-white text-gray-400 border-gray-300'}
                        transition-all duration-300
                        ${isClickable ? 'hover:shadow-lg' : ''}
                      `}
                    >
                      {isCompleted ? (
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <span className="text-sm sm:text-lg font-bold">{step.id}</span>
                      )}
                    </div>
                    
                    {/* Step name */}
                    <div className="mt-2 sm:mt-3 text-center">
                      <span 
                        className={`
                          text-xs sm:text-sm font-medium
                          ${isCompleted ? 'text-sky-600' : 
                            isCurrent ? 'text-sky-800' : 
                            'text-gray-400'}
                          ${isClickable ? 'hover:text-sky-700' : ''}
                        `}
                      >
                        {step.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Editing Notification */}
      {editingReport && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="text-yellow-800">
              You're editing a previous report. Changes will create a new version when finalized.
            </span>
          </div>
        </div>
      )}
      
      <Card className="mb-8 shadow-lg border-0">
        <CardHeader className="bg-gradient-to-r from-sky-600 to-cyan-500 text-white flex flex-col items-center justify-center p-4 sm:p-6 pb-4 sm:pb-6">
          <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold text-center">
            {currentStep === CalculatorStep.OrganizationDetails && "Organization Details"}
            {currentStep === CalculatorStep.CurrentMetrics && "Scheduling Performance"}
            {currentStep === CalculatorStep.TasScore && "Your TAS Score Results"}
            {currentStep === CalculatorStep.ROIAnalysis && (isRoosterBranded ? "Rooster ROI Analysis" : "ROI Analysis")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 md:p-8">
          <form onSubmit={handleCalculate}>
            {/* Step 1: Organization Details */}
            {currentStep === CalculatorStep.OrganizationDetails && (
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100 relative" style={{ overflow: 'visible' }}>
                {/* Contact Information Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Organization Name <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder="Enter your organization name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="email"
                        name="contactEmail"
                        value={formData.contactEmail}
                        onChange={handleInputChange}
                        className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder="Enter your email address"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Phone Number <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <input 
                        type="text"
                        name="contactPhone"
                        value={formData.contactPhone}
                        onChange={handleInputChange}
                        className={`w-full p-2.5 sm:p-3 border rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
                          phoneError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Enter your phone number (e.g., (555) 123-4567)"
                      />
                      {phoneError && (
                        <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Title/Role <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="text"
                        name="contactTitle"
                        value={formData.contactTitle}
                        onChange={handleInputChange}
                        className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder="Enter your title or role"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <hr className="my-6 border-gray-200" />

                {/* Organization Details Section */}
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Organization Details</h3>
                <div className="mb-5">
                  <label className="block text-sm font-medium mb-2 text-gray-700">Industry</label>
                  <IndustryDropdown
                    value={formData.industry}
                    onChange={(newIndustry) => {
                      // Get industry defaults
                      const industryDefaults = INDUSTRY_DEFAULTS[newIndustry as IndustryName];

                      // Auto-populate benchmark values for fields that haven't been manually edited
                      setFormData(prev => {
                        const updates: any = { industry: newIndustry };

                        // Only auto-fill if the field is empty or still has the default 0 value
                        if (industryDefaults) {
                          if (!prev.avgConfirmationTime || prev.avgConfirmationTime === 0) {
                            updates.avgConfirmationTime = industryDefaults.avgConfirmationTime;
                          }
                          if (!prev.adminTimePerHire || prev.adminTimePerHire === 0) {
                            updates.adminTimePerHire = industryDefaults.adminTimePerHire;
                          }
                          if (!prev.reschedulingRate || prev.reschedulingRate === 0) {
                            updates.reschedulingRate = industryDefaults.reschedulingRate;
                          }
                          if (!prev.interviewNoShowRate || prev.interviewNoShowRate === 0) {
                            updates.interviewNoShowRate = industryDefaults.interviewNoShowRate;
                          }
                          if (!prev.timeToFill || prev.timeToFill === 0) {
                            updates.timeToFill = industryDefaults.timeToFill;
                          }
                          if (!prev.costPerHire || prev.costPerHire === 0) {
                            updates.costPerHire = industryDefaults.costPerHire;
                          }
                          if (!prev.dailyVacancyCost || prev.dailyVacancyCost === 0) {
                            updates.dailyVacancyCost = industryDefaults.dailyVacancyCost;
                          }
                          if (!prev.candidateSatisfaction || prev.candidateSatisfaction === 0) {
                            updates.candidateSatisfaction = industryDefaults.candidateSatisfaction;
                          }
                          if (!prev.interviewerSatisfaction || prev.interviewerSatisfaction === 0) {
                            updates.interviewerSatisfaction = industryDefaults.interviewerSatisfaction;
                          }
                          if (!prev.interviewerAvailability || prev.interviewerAvailability === 0) {
                            updates.interviewerAvailability = industryDefaults.interviewerAvailability;
                          }

                          // No longer tracking benchmark usage - keeping it simple
                        }

                        return { ...prev, ...updates };
                      });
                    }}
                  />
                </div>
                
                {/* Input Mode Toggle */}
                <div className="mb-5">
                  <label className={`block text-sm font-medium mb-3 ${
                    !formData.industry ? 'text-gray-400' : 'text-gray-700'
                  }`}>
                    How would you like to specify your organization's size?
                  </label>
                  <div className={`flex rounded-lg overflow-hidden border ${
                    !formData.industry ? 'border-gray-200 opacity-50' : 'border-gray-300'
                  }`}>
                    <button
                      type="button"
                      onClick={() => handleInputModeChange('annualHires')}
                      disabled={!formData.industry || (!!fteCount && parseInt(fteCount) > 0)}
                      className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                        !formData.industry
                          ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                          : inputMode === 'annualHires'
                            ? 'bg-sky-600 text-white'
                            : (!!fteCount && parseInt(fteCount) > 0)
                              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      title={fteCount && parseInt(fteCount) > 0 ? 'Clear Total Employees field to switch' : ''}
                    >
                      Annual Hires
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputModeChange('fte')}
                      disabled={!formData.industry || (!!formData.annualHires && Number(formData.annualHires) > 0)}
                      className={`flex-1 py-2 px-4 text-sm font-medium transition-colors border-l ${
                        !formData.industry
                          ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                          : inputMode === 'fte'
                            ? 'border-gray-300 bg-sky-600 text-white'
                            : (!!formData.annualHires && Number(formData.annualHires) > 0)
                              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      title={formData.annualHires && Number(formData.annualHires) > 0 ? 'Clear Annual Hires field to switch' : ''}
                    >
                      Total Employees
                    </button>
                  </div>
                </div>
                
                {/* Conditional Input Field */}
                <div className="mb-5">
                  {inputMode === 'annualHires' ? (
                    <>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Annual Hires
                      </label>
                      <input 
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9,]*"
                        name="annualHires"
                        value={formatWithCommas(formData.annualHires)}
                        onChange={handleInputChange}
                        onFocus={handleInputSelection}
                        onClick={handleInputClick}
                        className={`w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base ${
                          !formData.industry ? 'bg-gray-50 cursor-not-allowed' : ''
                        }`}
                        disabled={!formData.industry}
                        placeholder={formData.industry ? "e.g., 1,000" : "Select industry first"}
                      />
                      {formData.annualHires && Number(formData.annualHires) < 100 && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2">
                          <span className="text-amber-600">⚠️</span>
                          <p className="text-xs text-amber-800">
                            Minimum 100 annual hires required.
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Total Full-Time Employees (FTE)
                      </label>
                      <input 
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9,]*"
                        name="fteCount"
                        value={formatWithCommas(fteCount)}
                        onChange={handleFTEChange}
                        onFocus={handleInputSelection}
                        onClick={handleInputClick}
                        className={`w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base ${
                          !formData.industry ? 'bg-gray-50 cursor-not-allowed' : ''
                        }`}
                        disabled={!formData.industry}
                        placeholder={formData.industry ? "e.g., 5,000" : "Select industry first"}
                      />
                      {fteCount && parseInt(fteCount) < getMinFTE(formData.industry) && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2">
                          <span className="text-amber-600">⚠️</span>
                          <p className="text-xs text-amber-800">
                            Minimum {getMinFTE(formData.industry).toLocaleString()} total employees required{getMinFTE(formData.industry) > 500 ? ' for this industry' : ''}.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Privacy Consent Checkbox */}
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                      className="mt-1 mr-3 h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded cursor-pointer"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      {isRoosterBranded 
                        ? "I agree to securely save my information to enable accurate calculations and allow Rooster to contact me about my results. I understand my data will be processed in accordance with industry-standard security practices and will not be shared with third parties."
                        : "I agree to securely save my information to enable accurate calculations. I understand my data will be processed in accordance with industry-standard security practices and will not be shared with third parties."
                      } <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>
                
                <div className="mt-6">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={async () => {
                        // Set loading state
                        setIsNavigating(true);
                        
                        try {
                          // Validate required contact fields
                          if (!formData.organizationName || !formData.contactName || 
                              !formData.contactEmail || !formData.contactTitle) {
                            alert('Please fill in all required contact information fields.');
                            return;
                          }
                          // Validate email format
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(formData.contactEmail)) {
                            alert('Please enter a valid email address.');
                            return;
                          }
                          // Check privacy consent
                          if (!privacyConsent) {
                            alert('Please accept the privacy notice to continue.');
                            return;
                          }
                          
                          // Small delay to show spinner before navigation
                          await new Promise(resolve => setTimeout(resolve, 100));
                          goToStep(CalculatorStep.CurrentMetrics);
                        } finally {
                          // Reset loading state
                          setIsNavigating(false);
                        }
                      }}
                      disabled={
                        isNavigating ||
                        !formData.organizationName ||
                        !formData.contactName ||
                        !formData.contactEmail ||
                        !formData.contactTitle ||
                        !formData.industry || 
                        !formData.annualHires ||
                        Number(formData.annualHires) < 100 ||
                        !privacyConsent
                      }
                      className={`px-4 sm:px-8 py-3 font-medium rounded-md shadow-md transition-all duration-200 text-sm sm:text-base min-h-[44px] flex items-center justify-center ${
                        (isNavigating || !formData.organizationName || !formData.contactName || 
                         !formData.contactEmail || !formData.contactTitle ||
                         !formData.industry || !formData.annualHires || Number(formData.annualHires) < 100 ||
                         !privacyConsent)
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-sky-600 to-cyan-500 text-white hover:from-sky-700 hover:to-cyan-600'
                      }`}
                      title={
                        isNavigating ? 'Processing...' :
                        !formData.organizationName ? 'Please enter organization name' :
                        !formData.contactName ? 'Please enter your name' :
                        !formData.contactEmail ? 'Please enter your email' :
                        !formData.contactTitle ? 'Please enter your title/role' :
                        !formData.industry ? 'Please select an industry' :
                        !formData.annualHires ? 'Please enter annual hires' :
                        Number(formData.annualHires) < 100 ? 'Minimum 100 annual hires required' :
                        !privacyConsent ? 'Please accept the privacy notice' :
                        ''
                      }
                    >
                      {isNavigating ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        'Next: Scheduling Performance'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Scheduling Performance */}
            {currentStep === CalculatorStep.CurrentMetrics && (
              <>
                <div className="space-y-6">
                  {/* Section 1: Scheduling Method - Featured at top */}
                  <div className="bg-gradient-to-r from-sky-50 to-cyan-50 p-6 rounded-lg border border-sky-200">
                    <h3 className="text-xl font-bold mb-2 text-gray-800">
                      <span className="text-sky-600 mr-2">①</span>How do you currently schedule interviews? <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">Select at least one method that applies to your organization (required)</p>
                    <div className="space-y-2">
                    <label className="flex items-start p-3 border border-gray-300 bg-white rounded hover:bg-gray-50 cursor-pointer transition-colors">
                      <input 
                        type="checkbox" 
                        value="manual"
                        checked={formData.schedulingMethods.includes('manual')}
                        onChange={() => handleSchedulingMethodChange('manual')}
                        className="mt-1 mr-3" 
                      />
                      <div>
                        <div className="font-medium text-gray-900">Manual Coordination</div>
                        <div className="text-xs text-gray-500">Email, phone, and calendar invites</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start p-3 border border-gray-300 bg-white rounded hover:bg-gray-50 cursor-pointer transition-colors">
                      <input 
                        type="checkbox" 
                        value="scheduling_links"
                        checked={formData.schedulingMethods.includes('scheduling_links')}
                        onChange={() => handleSchedulingMethodChange('scheduling_links')}
                        className="mt-1 mr-3" 
                      />
                      <div>
                        <div className="font-medium text-gray-900">Scheduling Links</div>
                        <div className="text-xs text-gray-500">Calendly, Doodle, or similar booking tools</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start p-3 border border-gray-300 bg-white rounded hover:bg-gray-50 cursor-pointer transition-colors">
                      <input 
                        type="checkbox" 
                        value="ats_native"
                        checked={formData.schedulingMethods.includes('ats_native')}
                        onChange={() => handleSchedulingMethodChange('ats_native')}
                        className="mt-1 mr-3" 
                      />
                      <div>
                        <div className="font-medium text-gray-900">ATS-Native Scheduling</div>
                        <div className="text-xs text-gray-500">Scheduling features built into your ATS</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start p-3 border border-gray-300 bg-white rounded hover:bg-gray-50 cursor-pointer transition-colors">
                      <input 
                        type="checkbox" 
                        value="scheduling_platform"
                        checked={formData.schedulingMethods.includes('scheduling_platform')}
                        onChange={() => handleSchedulingMethodChange('scheduling_platform')}
                        className="mt-1 mr-3" 
                      />
                      <div>
                        <div className="font-medium text-gray-900">Interview Scheduling Platform</div>
                        <div className="text-xs text-gray-500">ATS-integrated interview scheduling software</div>
                      </div>
                    </label>
                    </div>
                    
                    {/* Contextual feedback based on selection */}
                    {formData.schedulingMethods.length > 0 && (
                      <div className="mt-4 p-3 bg-white border-l-4 border-yellow-400 rounded">
                        <p className="text-sm text-gray-700">
                          {formData.schedulingMethods.length === 1 && (
                            <>
                              {formData.schedulingMethods.includes('manual') && 
                                "Manual coordination results in longer confirmation times and higher administrative burden. Rooster can significantly reduce these inefficiencies."}
                              {formData.schedulingMethods.includes('scheduling_links') && 
                                "Scheduling links help but operate separately from your ATS. Rooster integrates scheduling directly into your hiring workflow for seamless automation."}
                              {formData.schedulingMethods.includes('ats_native') && 
                                "ATS-native scheduling features are often basic and limited. Rooster provides advanced automation, intelligent optimization, and superior candidate experience."}
                              {formData.schedulingMethods.includes('scheduling_platform') && 
                                "You're using a dedicated scheduling platform. Rooster can still deliver improvements through more intelligent automation, better candidate experience, and deeper ATS integration."}
                            </>
                          )}
                          {formData.schedulingMethods.length > 1 && (
                            <>
                              Using multiple scheduling methods ({formData.schedulingMethods.length} selected) adds complexity and inefficiency to your process. 
                              Rooster provides a unified solution that eliminates the need for multiple tools and streamlines your entire scheduling workflow.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Section 2: Key Hiring Metrics */}
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-xl font-bold mb-2 text-gray-800 border-b pb-3 border-gray-200">
                      <span className="text-sky-600 mr-2">②</span>Current Hiring Metrics
                    </h3>
                    <p className="text-sm text-gray-600 mb-5">
                      Your organization's current hiring performance benchmarks.
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Time to Fill (days) <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('timeToFill')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9,]*"
                          name="timeToFill"
                          value={formatWithCommas(formData.timeToFill)}
                          onChange={handleInputChange}
                          onFocus={handleInputSelection}
                          onClick={handleInputClick}
                          className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-500 mt-1">From job posting to accepted offer</p>
                        {currentIndustryDefaults && (
                          <p className="text-xs text-blue-600 mt-1">
                            {formData.industry} Industry Standard: {currentIndustryDefaults.timeToFill} days
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Cost per Hire ($) <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('costPerHire')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9,]*"
                          name="costPerHire"
                          value={formatWithCommas(formData.costPerHire)}
                          onChange={handleInputChange}
                          onFocus={handleInputSelection}
                          onClick={handleInputClick}
                          className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-500 mt-1">Total recruiting expenses per hire</p>
                        {currentIndustryDefaults && (
                          <p className="text-xs text-blue-600 mt-1">
                            {formData.industry} Industry Standard: ${formatWithCommas(currentIndustryDefaults.costPerHire)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Connected Salary and Vacancy Cost Section */}
                  <SalaryVacancyGroup
                    avgSalary={formData.avgSalary}
                    dailyVacancyCost={formData.dailyVacancyCost}
                    industryName={formData.industry}
                    onSalaryChange={(value) => handleInputChange({ target: { name: 'avgSalary', value } } as any)}
                    onVacancyCostChange={(value) => handleInputChange({ target: { name: 'dailyVacancyCost', value } } as any)}
                    onCalculatorClick={() => setShowHiringCalculator(true)}
                    formatWithCommas={formatWithCommas}
                    handleInputSelection={handleInputSelection}
                    handleInputClick={handleInputClick}
                    calculationBasis={calculationBasis}
                  />

                  {/* Section 4: Scheduling Efficiency Metrics */}
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-xl font-bold mb-2 text-gray-800 border-b pb-3 border-gray-200">
                      <span className="text-sky-600 mr-2">④</span>Scheduling Process Efficiency
                    </h3>
                    <p className="text-sm text-gray-600 mb-5">
                      How efficiently your current scheduling process operates.
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Time to Schedule Interview (hours) <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('avgConfirmationTime')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9,]*"
                          name="avgConfirmationTime"
                          value={formatWithCommas(formData.avgConfirmationTime)}
                          onChange={handleInputChange}
                          onFocus={handleInputSelection}
                          onClick={handleInputClick}
                          className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base"
                          placeholder="e.g., 48"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          From interview request to confirmed appointment
                        </p>
                        {currentIndustryDefaults && (
                          <p className="text-xs text-blue-600 mt-1">
                            {formData.industry} Industry Standard: {currentIndustryDefaults.avgConfirmationTime} hours
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Scheduling Effort per Interview (min) <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('adminTimePerHire')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9,]*"
                          name="adminTimePerHire"
                          value={formatWithCommas(formData.adminTimePerHire)}
                          onChange={handleInputChange}
                          onFocus={handleInputSelection}
                          onClick={handleInputClick}
                          className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base"
                          placeholder="e.g., 120"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Total time spent on emails, calls, calendar coordination
                        </p>
                        {currentIndustryDefaults && (
                          <p className="text-xs text-blue-600 mt-1">
                            {formData.industry} Industry Standard: {currentIndustryDefaults.adminTimePerHire} minutes
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Rescheduling Rate (%) <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('reschedulingRate')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9,.]*"
                          name="reschedulingRate"
                          value={formatWithCommas(formData.reschedulingRate)}
                          onChange={handleInputChange}
                          onFocus={handleInputSelection}
                          onClick={handleInputClick}
                          className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base"
                          placeholder="e.g., 20"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          % of interviews that need to be rescheduled
                        </p>
                        {currentIndustryDefaults && (
                          <p className="text-xs text-blue-600 mt-1">
                            {formData.industry} Industry Standard: {currentIndustryDefaults.reschedulingRate}%
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          No-Show Rate (%) <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('interviewNoShowRate')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9,.]*"
                          name="interviewNoShowRate"
                          value={formatWithCommas(formData.interviewNoShowRate)}
                          onChange={handleInputChange}
                          onFocus={handleInputSelection}
                          onClick={handleInputClick}
                          className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 auto-select text-sm sm:text-base"
                          placeholder="e.g., 12"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          % of scheduled interviews where candidate doesn't attend
                        </p>
                        {currentIndustryDefaults && (
                          <p className="text-xs text-blue-600 mt-1">
                            {formData.industry} Industry Standard: {currentIndustryDefaults.interviewNoShowRate}%
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 5: Experience & Satisfaction */}
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-xl font-bold mb-2 text-gray-800 border-b pb-3 border-gray-200">
                      <span className="text-sky-600 mr-2">⑤</span>Experience & Satisfaction
                    </h3>
                    <p className="text-sm text-gray-600 mb-5">
                      Rate the scheduling experience from different perspectives.
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Candidate Scheduling Experience <span className="text-red-500">*</span>
                          <button
                            type="button"
                            onClick={() => handleHelpClick('candidateSatisfaction')}
                            className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </label>
                        <p className="text-xs text-gray-500 mb-3">How would candidates rate your scheduling process?</p>
                  <div className="mt-4 px-3 pb-8">
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      value={Number(formData.candidateSatisfaction)}
                      onChange={(value) => handleInputChange({ target: { name: 'candidateSatisfaction', value } } as any)}
                      marks={{
                        1: <span className="text-xs">1<br/>(Poor)</span>,
                        2: '2',
                        3: '3',
                        4: '4',
                        5: '5',
                        6: '6',
                        7: '7',
                        8: '8',
                        9: '9',
                        10: <span className="text-xs">10<br/>(Excellent)</span>
                      }}
                      trackStyle={{ backgroundColor: '#4f46e5', height: 8 }}
                      handleStyle={{ 
                        borderColor: '#4f46e5', 
                        backgroundColor: '#4f46e5',
                        height: 20, 
                        width: 20, 
                        marginTop: -6,
                        opacity: 1
                      }}
                      railStyle={{ backgroundColor: '#e5e7eb', height: 8 }}
                      dotStyle={{ display: 'none' }}
                    />
                  </div>
                  {currentIndustryDefaults && (
                    <p className="text-xs text-blue-600 mt-1 text-center">
                      {formData.industry} Industry Standard: {currentIndustryDefaults.candidateSatisfaction}/10
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Interviewer Scheduling Experience <span className="text-red-500">*</span>
                    <button
                      type="button"
                      onClick={() => handleHelpClick('interviewerSatisfaction')}
                      className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">How would interviewers rate your scheduling process?</p>
                  <div className="mt-4 px-3 pb-8">
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      value={Number(formData.interviewerSatisfaction)}
                      onChange={(value) => handleInputChange({ target: { name: 'interviewerSatisfaction', value } } as any)}
                      marks={{
                        1: <span className="text-xs">1<br/>(Poor)</span>,
                        2: '2',
                        3: '3',
                        4: '4',
                        5: '5',
                        6: '6',
                        7: '7',
                        8: '8',
                        9: '9',
                        10: <span className="text-xs">10<br/>(Excellent)</span>
                      }}
                      trackStyle={{ backgroundColor: '#4f46e5', height: 8 }}
                      handleStyle={{ 
                        borderColor: '#4f46e5', 
                        backgroundColor: '#4f46e5',
                        height: 20, 
                        width: 20, 
                        marginTop: -6,
                        opacity: 1
                      }}
                      railStyle={{ backgroundColor: '#e5e7eb', height: 8 }}
                      dotStyle={{ display: 'none' }}
                    />
                  </div>
                  {currentIndustryDefaults && (
                    <p className="text-xs text-blue-600 mt-1 text-center">
                      {formData.industry} Industry Standard: {currentIndustryDefaults.interviewerSatisfaction}/10
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Interviewer Calendar Availability <span className="text-red-500">*</span>
                    <button
                      type="button"
                      onClick={() => handleHelpClick('interviewerAvailability')}
                      className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">How easy is it to find available time slots?</p>
                  <div className="mt-4 px-3 pb-8">
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      value={Number(formData.interviewerAvailability)}
                      onChange={(value) => handleInputChange({ target: { name: 'interviewerAvailability', value } } as any)}
                      marks={{
                        1: <span className="text-xs">1<br/>(Difficult)</span>,
                        2: '2',
                        3: '3',
                        4: '4',
                        5: '5',
                        6: '6',
                        7: '7',
                        8: '8',
                        9: '9',
                        10: <span className="text-xs">10<br/>(Easy)</span>
                      }}
                      trackStyle={{ backgroundColor: '#4f46e5', height: 8 }}
                      handleStyle={{ 
                        borderColor: '#4f46e5', 
                        backgroundColor: '#4f46e5',
                        height: 20, 
                        width: 20, 
                        marginTop: -6,
                        opacity: 1
                      }}
                      railStyle={{ backgroundColor: '#e5e7eb', height: 8 }}
                      dotStyle={{ display: 'none' }}
                    />
                  </div>
                  {currentIndustryDefaults && (
                    <p className="text-xs text-blue-600 mt-1 text-center">
                      {formData.industry} Industry Standard: {currentIndustryDefaults.interviewerAvailability}/10
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-8">
                  <button
                    type="button"
                    onClick={() => goToStep(CalculatorStep.OrganizationDetails)}
                    className="px-4 sm:px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 transition-all duration-200 text-sm sm:text-base min-h-[44px]"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!validateCurrentMetrics().isValid || isCalculating}
                    className={`px-4 sm:px-8 py-3 font-medium rounded-md shadow-md transition-all duration-200 text-sm sm:text-base min-h-[44px] flex items-center justify-center ${
                      validateCurrentMetrics().isValid && !isCalculating
                        ? 'bg-gradient-to-r from-sky-600 to-cyan-500 text-white hover:from-sky-700 hover:to-cyan-600 cursor-pointer' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    title={isCalculating ? 'Calculating...' : !validateCurrentMetrics().isValid ? 'Please complete all required fields' : ''}
                  >
                    {isCalculating ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Calculating...
                      </>
                    ) : (
                      'Calculate TAS Score'
                    )}
                  </button>
                </div>
              </>
            )}
            
            {/* Step 3: TAS Score */}
            {currentStep === CalculatorStep.TasScore && results.currentTAS > 0 && (
              <div className="flex flex-col items-center" data-section="tas-score">
                <div className="mb-10 text-center">
                  <div className="inline-flex items-center justify-center bg-gradient-to-br from-sky-50 to-cyan-50 rounded-full w-36 h-36 mb-6 shadow-inner border border-sky-100">
                    <div className="text-center">
                      <p className="text-5xl font-bold bg-gradient-to-r from-sky-600 to-cyan-500 bg-clip-text text-transparent">{results.currentTAS}</p>
                      <p className="text-sm text-gray-500">out of 100</p>
                    </div>
                  </div>
                  
                  <p className="text-xl text-gray-700 font-medium">
                    {results.currentTAS < 40 ? 
                      "Your scheduling process needs improvement." :
                      results.currentTAS < 60 ? 
                      "Your scheduling efficiency is average for your industry." :
                      results.currentTAS < 80 ?
                      "Your scheduling process is good, above industry standards." :
                      "Your scheduling process is excellent, among the top performers."}
                  </p>
                </div>
                
                {/* TAS Components Chart with Interactive Tooltips */}
                <div className="w-full max-w-2xl mx-auto mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                  <div className="flex justify-between items-center mb-4 border-b pb-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-800">
                      <span className="text-sky-600 mr-2">📊</span>TAS Score Components
                    </h3>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    Your TAS Score reflects your scheduling efficiency across these weighted components. 
                    Higher scores in high-weight components will impact your overall score more significantly. <span className="font-medium text-sky-600">Click the arrow on any component below to see specific Rooster features that will improve that score.</span>
                  </p>
                  
                  <div className="space-y-4">
                    {(() => {
                      // Find the lowest scoring component
                      const componentData = getComponentData();
                      const lowestScore = Math.min(...componentData.map(c => c.value));
                      
                      return componentData.map((component, index) => {
                        // Get the component key based on the name
                      const componentKey = component.name.includes('SV') ? 'schedulingVelocity' :
                                         component.name.includes('SE') ? 'schedulingEfficiency' :
                                         component.name.includes('CE') ? 'candidateExperience' :
                                         component.name.includes('SI') ? 'schedulingIntegrity' :
                                         'schedulingUtilization';
                        
                        const isExpanded = expandedComponents.has(componentKey);
                        const isLowestScore = component.value === lowestScore && !isExpanded;
                        
                        // Get all features for this component
                        const allFeatures = getTopFeaturesForComponent(componentKey as any, 100); // Get all relevant features
                        const showAll = showAllFeatures.has(componentKey);
                        const topFeatures = showAll ? allFeatures : allFeatures.slice(0, 3); // Show top 3 by default
                        
                        return (
                      <div key={index} className="relative border border-gray-200 rounded-lg p-4 hover:border-sky-300 transition-all">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center flex-1">
                            <span className="font-medium text-sm flex items-center">
                              {component.name}
                              <InfoTooltip
                                title={component.name}
                                description={component.description}
                                additionalInfo={
                                  <>
                                    <p className="text-gray-700 mb-1"><span className="font-bold text-sky-700">Why it matters:</span> {component.importance}</p>
                                    <p className="text-gray-700 mb-1"><span className="font-bold text-sky-700">How it's calculated:</span> {component.calculation}</p>
                                    <p className="text-gray-700 mb-1"><span className="font-bold text-sky-700">Impact on total score:</span> This component contributes {component.weight} to your overall TAS score.</p>
                                    <span className="font-bold text-xs text-sky-700">Formula: </span>
                                    <span className="text-xs font-mono bg-gray-50 p-1 rounded">{component.formula}</span>
                                  </>
                                }
                                topFeatures={topFeatures.slice(0, 2).map(f => ({
                                  name: f.name,
                                  impact: f.impacts[componentKey as keyof typeof f.impacts] || 0,
                                  icon: f.icon
                                }))}
                              />
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                if (isExpanded) {
                                  // Clicking expanded component closes it
                                  setExpandedComponents(new Set());
                                } else {
                                  // Clicking any component opens only that one (accordion behavior)
                                  setExpandedComponents(new Set([componentKey]));
                                  
                                  // Capture the element reference before the timeout
                                  const cardElement = e.currentTarget.closest('.border.border-gray-200.rounded-lg');
                                  
                                  // Auto-scroll to center the expanded component
                                  if (cardElement) {
                                    setTimeout(() => {
                                      cardElement.scrollIntoView({ 
                                        behavior: 'smooth', 
                                        block: 'center'
                                      });
                                    }, 100); // Small delay to allow expansion animation to start
                                  }
                                }
                              }}
                              className={`text-sky-600 hover:text-sky-700 transition-colors p-1 flex items-center gap-1 ${isLowestScore ? 'pulse-animation' : ''}`}
                              aria-label={isExpanded ? "Hide improvements" : "Show improvements"}
                            >
                              {!isExpanded && (
                                <span className="text-xs font-medium">See improvements</span>
                              )}
                              <svg 
                                className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        {/* Progress bar with gradient background */}
                        <div 
                          className="w-full h-6 rounded-md overflow-hidden mb-2"
                          style={{
                            background: `linear-gradient(to right, 
                              rgb(254 226 226) 0%,
                              rgb(254 249 195) 40%,
                              rgb(220 252 231) 60%,
                              rgb(219 234 254) 80%,
                              rgb(219 234 254) 100%)`
                          }}
                        >
                          <div 
                            className="h-6 rounded-md transition-all duration-500 relative flex items-center justify-between px-2 text-xs text-white font-medium" 
                            style={{ 
                              width: `${safePercentage(component.value > 0 ? Math.max(component.value, 15) : 0)}%`,
                              backgroundColor: component.color 
                            }}
                          >
                            <span className="font-semibold">{component.value}%</span>
                          </div>
                        </div>
                        
                        {/* Expandable Features Section */}
                        <div 
                          className={`expandable-content ${isExpanded ? 'expanded' : ''}`}
                        >
                          <div className="border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2 pt-3">Rooster Features to Improve {component.name.split(' (')[0]}:</p>
                            <div className="space-y-2">
                              {topFeatures.map((feature, idx) => {
                                const impact = feature.impacts[componentKey as keyof typeof feature.impacts] || 0;
                                const impactLevel = impact >= 70 ? 'high' : impact >= 40 ? 'medium' : 'low';
                                
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                                    <span className="text-lg mt-0.5">{feature.icon}</span>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">{feature.name}</span>
                                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                          impactLevel === 'high' ? 'bg-green-100 text-green-700' :
                                          impactLevel === 'medium' ? 'bg-blue-100 text-blue-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {impact}% impact
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-600 mt-1">{feature.whatItDoes}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {allFeatures.length > 3 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newShowAll = new Set(showAllFeatures);
                                  if (showAll) {
                                    newShowAll.delete(componentKey);
                                  } else {
                                    newShowAll.add(componentKey);
                                  }
                                  setShowAllFeatures(newShowAll);
                                }}
                                className="mt-3 text-xs text-sky-600 hover:text-sky-700 font-medium"
                              >
                                {showAll ? 'Show less' : `Show ${allFeatures.length - 3} more`}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
                  </div>

                  {/* Legend */}
                  <div className="mt-8 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">What these scores mean:</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-red-100 mr-2"></div>
                        <span className="text-gray-600">0-40: Needs improvement</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-yellow-100 mr-2"></div>
                        <span className="text-gray-600">41-60: Average</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-100 mr-2"></div>
                        <span className="text-gray-600">61-80: Good</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-100 mr-2"></div>
                        <span className="text-gray-600">81-100: Excellent</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* TAS Benchmark Comparison - Clean Design */}
                <div className="w-full max-w-2xl mx-auto mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                  <div className="flex justify-between items-center mb-4 border-b pb-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-800">
                      <span className="text-sky-600 mr-2">📊</span>How You Compare
                    </h3>
                    <div className="text-xs text-gray-500 italic">
                      {results.currentTAS > calculateTopPerformerTAS(formData.industry) - 5 ? 
                        "Outstanding performance!" : 
                        `${calculateTopPerformerTAS(formData.industry) - results.currentTAS} points to top tier`}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    See how your TAS Score compares to {formData.industry} peers and top performers.
                  </p>

                  {/* Visual Bar with Gradient */}
                  <div className="relative mb-8 pt-10">
                    {/* Your Score Label - positioned above the bar */}
                    <div 
                      className="absolute text-center transition-all duration-1000 z-20"
                      style={{ 
                        left: `${safePercentage(results.currentTAS)}%`,
                        transform: 'translateX(-50%)',
                        top: '0'
                      }}
                    >
                      <div className="text-xs font-medium text-sky-600">Your Score</div>
                      <div className="text-sm font-bold text-sky-600">{results.currentTAS}</div>
                    </div>
                    
                    <div 
                      className="relative w-full h-12 rounded-lg overflow-hidden border border-gray-200"
                      style={{
                        background: `linear-gradient(to right, 
                          rgb(254 226 226) 0%,
                          rgb(254 249 195) 40%,
                          rgb(220 252 231) 60%,
                          rgb(219 234 254) 80%,
                          rgb(219 234 254) 100%)`
                      }}
                    >
                      {/* Markers */}
                      {/* Industry Average Marker */}
                      <div 
                        className="absolute inset-y-0 w-0.5 border-l border-dashed border-gray-400 transition-all duration-1000 group"
                        style={{ left: `${safePercentage(calculateIndustryTAS(formData.industry))}%` }}
                      >
                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-transparent border-b-gray-400"></div>
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          {formData.industry} Avg: {calculateIndustryTAS(formData.industry)}
                        </div>
                      </div>
                      
                      {/* Your Score Marker */}
                      <div 
                        className="absolute inset-y-0 w-1.5 bg-sky-600 shadow-lg transition-all duration-1000 z-10"
                        style={{ left: `calc(${safePercentage(results.currentTAS)}% - 0.1875rem)` }}
                      >
                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-transparent border-b-sky-600"></div>
                      </div>
                      
                      {/* Top Performers Marker */}
                      <div 
                        className="absolute inset-y-0 w-1 bg-green-500 transition-all duration-1000 group"
                        style={{ left: `${safePercentage(calculateTopPerformerTAS(formData.industry))}%` }}
                      >
                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-transparent border-b-green-500"></div>
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Top Performers: {calculateTopPerformerTAS(formData.industry)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Scale labels */}
                    <div className="relative mt-2 text-xs text-gray-500 h-4">
                      <span className="absolute left-0">Poor</span>
                      <span 
                        className="absolute transform -translate-x-1/2"
                        style={{ left: `${safePercentage(calculateIndustryTAS(formData.industry))}%` }}
                      >
                        Average
                      </span>
                      <span className="absolute right-0">Excellent</span>
                    </div>
                  </div>
                  
                  <div className="mb-8"></div>

                  {/* Legend Table */}
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm min-w-[350px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-4 py-2 text-left font-medium text-gray-700">Benchmark</th>
                          <th className="px-3 sm:px-4 py-2 text-center font-medium text-gray-700">Score</th>
                          <th className="px-3 sm:px-4 py-2 text-right font-medium text-gray-700">Difference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr className="bg-sky-50">
                          <td className="px-3 sm:px-4 py-2 sm:py-3">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-sky-600 rounded-sm mr-2"></div>
                              <span className="font-medium">Your Score</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-center font-bold text-sky-600">{results.currentTAS}</td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-right text-gray-600">—</td>
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 sm:py-3">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-gray-400 rounded-sm mr-2"></div>
                              <span>{formData.industry} Average</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-center">{calculateIndustryTAS(formData.industry)}</td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-right">
                            <span className={results.currentTAS > calculateIndustryTAS(formData.industry) ? "text-green-600" : "text-red-600"}>
                              {results.currentTAS > calculateIndustryTAS(formData.industry) ? "+" : ""}
                              {results.currentTAS - calculateIndustryTAS(formData.industry)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 sm:py-3">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-green-500 rounded-sm mr-2"></div>
                              <span>Top {formData.industry} Performers</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-center">{calculateTopPerformerTAS(formData.industry)}</td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-right">
                            <span className={results.currentTAS > calculateTopPerformerTAS(formData.industry) ? "text-green-600" : "text-orange-600"}>
                              {results.currentTAS > calculateTopPerformerTAS(formData.industry) ? "+" : ""}
                              {results.currentTAS - calculateTopPerformerTAS(formData.industry)}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  
                  {/* Performance Analysis */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">vs {formData.industry} Average:</span>
                        <span className={`ml-2 font-bold ${results.currentTAS > calculateIndustryTAS(formData.industry) ? 'text-green-600' : 'text-red-600'}`}>
                          {results.currentTAS > calculateIndustryTAS(formData.industry) ? '+' : ''}{results.currentTAS - calculateIndustryTAS(formData.industry)} points
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Room for Improvement:</span>
                        <span className="ml-2 font-bold text-sky-600">
                          {Math.max(0, calculateTopPerformerTAS(formData.industry) - results.currentTAS)}+ points
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TAS Report Finalization Section - Two-State Design (only for TAS-only modes) */}
                {!hasROIAnalysis && (
                  <div className={`transition-all duration-500 ease-in-out ${reportFinalized ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-r from-sky-50 to-cyan-50 border-sky-100'} p-8 rounded-lg mb-8 border shadow-sm`}>
                    {!reportFinalized ? (
                      // State 1: Pre-Finalization
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">Complete Your TAS Analysis</h3>
                        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                          Review your TAS score and insights above. You can adjust any parameters to see how different 
                          factors affect your scheduling performance. When you're satisfied with your analysis, finalize your report.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                          <button
                            type="button"
                            onClick={() => goToStep(CalculatorStep.CurrentMetrics)}
                            className="px-4 sm:px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 transition-all duration-200 text-sm sm:text-base min-h-[44px]"
                          >
                            ← Back
                          </button>
                          <button
                            type="button"
                            onClick={finalizeTasReport}
                            disabled={sessionSaving}
                            className="px-10 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold rounded-md hover:from-red-700 hover:to-red-600 shadow-lg transition-all duration-200 flex items-center justify-center"
                          >
                            {sessionSaving ? (
                              <>
                                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Finalizing...
                              </>
                            ) : (
                              <>Finalize Report →</>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // State 2: Post-Finalization
                      <div className="text-center">
                        <div className="flex justify-center mb-3">
                          <div className="bg-green-100 rounded-full p-3">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Your TAS Report is Ready</h3>
                        
                        {/* Report Link Display - Enhanced */}
                        {currentReportId && (
                          <div className="mb-6 p-5 bg-gradient-to-br from-blue-50 to-sky-50 border border-sky-200 rounded-lg max-w-xl mx-auto shadow-sm">
                            <div className="flex items-start gap-2 mb-3">
                              {/* Bookmark Icon */}
                              <svg className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                              </svg>
                              <div className="flex-1">
                                <p className="text-base font-medium text-gray-700">Your permanent TAS report link</p>
                                <p className="text-sm text-gray-500 mt-0.5">Bookmark or save it - this link never expires</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between bg-white p-3 rounded border border-gray-200">
                              <code className="text-sm text-gray-700 truncate flex-1 font-mono">
                                {window.location.origin}/tas-report/{currentReportId}
                              </code>
                              <button
                                type="button"
                                onClick={() => {
                                  const url = `${window.location.origin}/tas-report/${currentReportId}`;
                                  navigator.clipboard.writeText(url).then(() => {
                                    // Show temporary success message with checkmark
                                    const btn = document.getElementById('copy-tas-btn');
                                    if (btn) {
                                      const originalHTML = btn.innerHTML;
                                      btn.innerHTML = `
                                        <svg class="w-4 h-4 mr-1.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        Copied!
                                      `;
                                      btn.classList.add('bg-green-600', 'hover:bg-green-700');
                                      btn.classList.remove('bg-sky-600', 'hover:bg-sky-700');
                                      setTimeout(() => {
                                        btn.innerHTML = originalHTML;
                                        btn.classList.remove('bg-green-600', 'hover:bg-green-700');
                                        btn.classList.add('bg-sky-600', 'hover:bg-sky-700');
                                      }, 2000);
                                    }
                                  }).catch(() => {
                                    alert('Failed to copy. Please select and copy the URL manually.');
                                  });
                                }}
                                id="copy-tas-btn"
                                className="ml-2 px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded hover:bg-sky-700 transition-all duration-200 flex items-center"
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy Link
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Report Actions */}
                        <div className="mb-6 flex justify-center gap-4">
                          <button
                            type="button"
                            onClick={() => goToStep(CalculatorStep.CurrentMetrics)}
                            className="px-4 sm:px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 transition-all duration-200 text-sm sm:text-base min-h-[44px]"
                          >
                            Edit Report
                          </button>
                          
                          <button
                            type="button"
                            onClick={downloadTasReport}
                            disabled={isGeneratingReport}
                            className={`px-8 py-3 text-white font-medium rounded-md shadow-md transition-all duration-200 flex items-center justify-center ${
                              isGeneratingReport 
                                ? 'bg-sky-500 cursor-not-allowed opacity-80' 
                                : 'bg-gradient-to-r from-sky-600 to-cyan-500 hover:from-sky-700 hover:to-cyan-600'
                            }`}
                          >
                            {isGeneratingReport ? (
                              <>
                                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating Report...
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Report
                              </>
                            )}
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => {
                              if (currentReportId) {
                                setEmailDialogOpen(true);
                              }
                            }}
                            disabled={!currentReportId}
                            className="px-8 py-3 text-white font-medium rounded-md shadow-md transition-all duration-200 flex items-center justify-center bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Email Report
                          </button>
                        </div>
                        
                        {/* Divider */}
                        <div className="my-6 border-t border-gray-200"></div>
                        
                        {/* Call to Action */}
                        <div className="mt-6">
                          <h4 className="text-lg font-semibold text-gray-800 mb-2">Ready to improve your TAS score?</h4>
                          <p className="text-sm text-gray-600 mb-4">
                            Schedule a personalized demo to see how Rooster can optimize your interview scheduling process.
                          </p>
                          <a
                            href="https://rooster.us/demo"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-10 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-md hover:from-green-700 hover:to-green-600 shadow-lg transition-all duration-200"
                          >
                            Schedule Demo with Rooster →
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ROI Continuation Section - For versions with ROI Analysis */}
                {hasROIAnalysis && !reportFinalized && (
                  <div className="bg-gradient-to-r from-sky-50 to-cyan-50 border-sky-100 p-8 rounded-lg mb-8 border shadow-sm">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-800 mb-3">Ready to See Your Potential Savings?</h3>
                      <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                        Now that you have your TAS score, let's calculate the financial impact of improving your scheduling efficiency. 
                        See how much time and money you could save with optimized interview scheduling.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => goToStep(CalculatorStep.CurrentMetrics)}
                          className="px-4 sm:px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 transition-all duration-200 text-sm sm:text-base min-h-[44px]"
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            // Set navigating state
                            setIsNavigating(true);
                            
                            try {
                              // Calculate ROI first
                              calculateCompleteROI();
                              
                              // Small delay to ensure state updates before navigation
                              await new Promise(resolve => setTimeout(resolve, 200));
                              await goToStep(CalculatorStep.ROIAnalysis);
                              window.scrollTo(0, 0);
                            } finally {
                              // Reset navigating state
                              setIsNavigating(false);
                            }
                          }}
                          disabled={isNavigating}
                          className={`px-10 py-3 font-bold rounded-md shadow-lg transition-all duration-200 flex items-center justify-center ${
                            isNavigating 
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-700 hover:to-green-600'
                          }`}
                        >
                          {isNavigating ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            'See Your Potential Savings →'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 4: ROI Analysis - Loading State */}
            {currentStep === CalculatorStep.ROIAnalysis && hasROIAnalysis && (!results.metrics?.current || !results.metrics?.improved) && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600 mb-4"></div>
                <p className="text-gray-600">Calculating your ROI analysis...</p>
              </div>
            )}
            
            {/* Step 4: ROI Analysis - Content */}
            {currentStep === CalculatorStep.ROIAnalysis && hasROIAnalysis && results.metrics?.current && results.metrics?.improved && (
              <div>
                {/* ROI Analysis content */}
                
                {/* Full width How Rooster improves section - MOVED TO TOP */}
                <div className="mb-8 bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 border-b pb-2 border-gray-200">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-0">
                      <span className="text-sky-600 mr-2">↗</span>{isRoosterBranded ? "How Rooster improves your bottom line" : "How to improve your bottom line"}
                    </h3>
                    <div className="text-xs text-sky-500 italic">Adjust your improvement target</div>
                  </div>

                  <p className="text-xs sm:text-sm text-gray-600 mb-6">
                    <span className="font-medium">Drag the purple slider or click anywhere on the bar</span> to adjust your target TAS score.
                  </p>

                  <div className="mb-8">
                    {/* Visual Bar with Gradient - EXACT copy from "How You Compare" */}
                    <div className="relative mb-8 pt-10 pb-2">
                      {/* Your Score Label - positioned above the bar */}
                      <div 
                        className="absolute text-center transition-all duration-1000 z-20"
                        style={{ 
                          left: `${safePercentage(results.currentTAS)}%`,
                          transform: 'translateX(-50%)',
                          top: '-5px'
                        }}
                      >
                        <div className="text-xs font-medium text-sky-600">Your Score</div>
                        <div className="text-sm font-bold text-sky-600">{results.currentTAS}</div>
                      </div>
                      
                      
                      <div 
                        className="relative w-full h-12 rounded-lg overflow-visible border border-gray-200"
                        style={{
                          background: `linear-gradient(to right, 
                            rgb(254 202 202) 0%,
                            rgb(254 240 138) 40%,
                            rgb(187 247 208) 60%,
                            rgb(191 219 254) 80%,
                            rgb(191 219 254) 100%)`
                        }}
                        onClick={(e) => {
                          // Click to position functionality
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const currentTAS = safePercentage(results.currentTAS);
                          const percentage = Math.min(100, Math.max(currentTAS, (x / rect.width) * 100));
                          const newImprovement = Math.round(percentage - currentTAS);
                          
                          if (newImprovement >= 0) {
                            setFormData(prev => ({...prev, tasImprovement: newImprovement}));
                            calculateCompleteROI({ tasImprovement: newImprovement });
                            setReportFinalized(false);
                            clearPDFAndReportWindow();  // Clear old PDF and report window
                          }
                        }}
                      >
                        {/* Track highlight from current to improved score */}
                        <div 
                          className="absolute inset-y-0 bg-cyan-500 opacity-50 transition-all duration-300"
                          style={{ 
                            left: `${safePercentage(results.currentTAS)}%`,
                            width: `${safePercentage(getNumericValue(formData.tasImprovement, 20))}%`
                          }}
                        />
                        
                        {/* Rooster Booster Zone Pattern Overlay */}
                        <div 
                          className="absolute inset-y-0 pointer-events-none rounded-r-lg"
                          style={{ 
                            left: `${safePercentage(calculateTopPerformerTAS(formData.industry))}%`,
                            width: `${safePercentage(100 - calculateTopPerformerTAS(formData.industry))}%`,
                            backgroundImage: `repeating-linear-gradient(
                              45deg,
                              transparent,
                              transparent 4px,
                              rgba(34, 197, 94, 0.12) 4px,
                              rgba(34, 197, 94, 0.12) 8px
                            )`
                          }}
                        />
                        
                        {/* Rooster Zone Label inside the pattern */}
                        <div
                          className="absolute text-xs font-semibold pointer-events-none flex items-center bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-sm whitespace-nowrap z-20 border border-gray-200/50"
                          style={{ 
                            left: `${safePercentage(calculateTopPerformerTAS(formData.industry) + ((100 - calculateTopPerformerTAS(formData.industry)) / 2))}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#059669' // emerald-600 for better contrast
                          }}
                        >
                          {isRoosterBranded && <img src={roosterIcon} alt="Rooster" className="w-4 h-4 mr-1.5 flex-shrink-0" />}
                          <span>
                            {isRoosterBranded 
                              ? ((100 - calculateTopPerformerTAS(formData.industry)) > 25 
                                  ? "Rooster Booster Zone" 
                                  : "Zone")
                              : ((100 - calculateTopPerformerTAS(formData.industry)) > 25 
                                  ? "Top Performer Zone" 
                                  : "Zone")
                            }
                          </span>
                        </div>
                        
                        {/* Industry Average Marker */}
                        <div 
                          className="absolute inset-y-0 w-0.5 border-l border-dashed border-gray-400 transition-all duration-1000 group"
                          style={{ left: `${safePercentage(calculateIndustryTAS(formData.industry))}%` }}
                        >
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            {formData.industry} Avg: {calculateIndustryTAS(formData.industry)}
                          </div>
                        </div>
                        
                        {/* Your Score Marker */}
                        <div 
                          className="absolute inset-y-0 w-1.5 bg-sky-600 shadow-lg transition-all duration-300 z-10 rounded-t"
                          style={{ left: `calc(${safePercentage(results.currentTAS)}% - 0.1875rem)` }}
                        >
                        </div>
                        
                        {/* Improved Score Slider Thumb - Mobile Optimized */}
                        <div 
                          className="absolute top-1/2 transform -translate-y-1/2 z-30"
                          style={{ left: `${safePercentage(results.currentTAS + getNumericValue(formData.tasImprovement, 20))}%` }}
                        >
                          {/* Slider thumb with larger touch target for mobile */}
                          <div
                            className="relative w-11 h-11 sm:w-8 sm:h-8 bg-cyan-600 rounded-full shadow-lg cursor-pointer touch-manipulation transform -translate-x-1/2 active:scale-110 transition-transform group"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              
                              // Store the parent bar element reference
                              const barElement = e.currentTarget.closest('.relative.w-full') as HTMLElement;
                              if (!barElement) return;
                              
                              const rect = barElement.getBoundingClientRect();
                              
                              const handleMouseMove = (moveEvent: MouseEvent) => {
                                const x = moveEvent.clientX - rect.left;
                                const percentage = Math.min(100, Math.max(results.currentTAS, (x / rect.width) * 100));
                                const newImprovement = Math.round(percentage - results.currentTAS);
                                
                                if (newImprovement >= 0) {
                                  setFormData(prev => ({...prev, tasImprovement: newImprovement}));
                                  calculateCompleteROI({ tasImprovement: newImprovement });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }
                              };
                              
                              const handleMouseUp = () => {
                                document.removeEventListener('mousemove', handleMouseMove);
                                document.removeEventListener('mouseup', handleMouseUp);
                                document.body.style.cursor = '';
                              };
                              
                              document.body.style.cursor = 'grabbing';
                              document.addEventListener('mousemove', handleMouseMove);
                              document.addEventListener('mouseup', handleMouseUp);
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              
                              const barElement = e.currentTarget.closest('.relative.w-full') as HTMLElement;
                              if (!barElement) return;
                              
                              const rect = barElement.getBoundingClientRect();
                              
                              const handleTouchMove = (moveEvent: TouchEvent) => {
                                moveEvent.preventDefault();
                                const touch = moveEvent.touches[0];
                                const x = touch.clientX - rect.left;
                                const percentage = Math.min(100, Math.max(results.currentTAS, (x / rect.width) * 100));
                                const newImprovement = Math.round(percentage - results.currentTAS);
                                
                                if (newImprovement >= 0) {
                                  setFormData(prev => ({...prev, tasImprovement: newImprovement}));
                                  calculateCompleteROI({ tasImprovement: newImprovement });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();
                                }
                              };
                              
                              const handleTouchEnd = () => {
                                document.removeEventListener('touchmove', handleTouchMove);
                                document.removeEventListener('touchend', handleTouchEnd);
                              };
                              
                              document.addEventListener('touchmove', handleTouchMove, { passive: false });
                              document.addEventListener('touchend', handleTouchEnd);
                            }}
                          >
                            {/* Inner white circle for better visibility */}
                            <div className="absolute inset-2 sm:inset-1 bg-white rounded-full"></div>
                            {/* Purple center dot */}
                            <div className="absolute inset-3 sm:inset-2 bg-cyan-600 rounded-full"></div>
                            {/* Value tooltip - visible on mobile, hover on desktop */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-cyan-700 text-white text-xs rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              Score: {results.currentTAS + getNumericValue(formData.tasImprovement, 20)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Top Performers Marker */}
                        <div 
                          className="absolute inset-y-0 w-1 bg-emerald-500 transition-all duration-1000 group rounded-t"
                          style={{ left: `${safePercentage(calculateTopPerformerTAS(formData.industry))}%` }}
                        >
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Top Performers: {calculateTopPerformerTAS(formData.industry)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Scale labels */}
                      <div className="relative mt-2 text-xs text-gray-500 h-4">
                        <span className="absolute left-0">Poor</span>
                        <span 
                          className="absolute transform -translate-x-1/2"
                          style={{ left: `${safePercentage(calculateIndustryTAS(formData.industry))}%` }}
                        >
                          {formData.industry} Avg
                        </span>
                        <span className="absolute right-0">Excellent</span>
                      </div>
                    </div>

                    {/* Responsive spacing */}
                    <div className="mt-8 mb-8">
                      <div className="max-w-full sm:max-w-md mx-auto px-4 sm:px-0">
                        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
                          <div className="text-center py-2 px-3 sm:px-4 bg-sky-100 text-sky-800 text-xs sm:text-sm font-medium border-b border-sky-200">
                            TAS Score Calculation
                          </div>
                          <div className="p-3 sm:p-4">
                            <div className="flex items-center justify-between">
                              {/* Numbers row with responsive text */}
                              <div className="flex-1 text-center flex justify-center items-center">
                                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">{results.currentTAS}</div>
                              </div>
                              <div className="px-1 sm:px-2 flex justify-center items-center">
                                <div className="text-lg sm:text-xl lg:text-2xl font-normal text-sky-400">+</div>
                              </div>
                              <div className="flex-1 text-center flex justify-center items-center">
                                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-sky-600">{getNumericValue(formData.tasImprovement, 20)}</div>
                              </div>
                              <div className="px-1 sm:px-2 flex justify-center items-center">
                                <div className="text-lg sm:text-xl lg:text-2xl font-normal text-sky-400">=</div>
                              </div>
                              <div className="flex-1 text-center bg-sky-50 py-1 sm:py-2 rounded flex justify-center items-center">
                                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-sky-700">{results.currentTAS + getNumericValue(formData.tasImprovement, 20)}</div>
                              </div>
                            </div>

                            {/* Labels row */}
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex-1 text-center">
                                <div className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500">Current TAS</div>
                              </div>
                              <div className="px-1 sm:px-2"></div>
                              <div className="flex-1 text-center">
                                <div className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500">Improvement</div>
                              </div>
                              <div className="px-1 sm:px-2"></div>
                              <div className="flex-1 text-center">
                                <div className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500">New TAS</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feature Recommendations - Moved here for better flow */}
                    {isRoosterBranded && (
                      <div className="mb-8">
                        <FeatureRecommendations 
                          tasComponents={results.tasComponents}
                          currentTAS={results.currentTAS}
                          projectedTAS={results.currentTAS + getNumericValue(formData.tasImprovement, 20)}
                          tasImprovement={getNumericValue(formData.tasImprovement, 20)}
                        />
                      </div>
                    )}

                    {/* Key Metrics Table */}
                    <div className="mb-2 mt-8">
                      <p className="text-xs text-gray-600 italic">The metrics below show improvements for each individual hire. Your annual impact = per-hire savings × {getNumericValue(formData.annualHires).toLocaleString()} hires/year.</p>
                    </div>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full text-xs sm:text-sm mb-0 min-w-[600px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-3 text-left font-medium text-gray-700">Key Metrics</th>
                          <th className="p-3 text-center font-medium text-gray-700">
                            <div>Current</div>
                            <div className="text-xs font-normal text-gray-500">(per hire)</div>
                          </th>
                          <th className="p-3 text-center font-medium text-sky-700">
                            <div>{isRoosterBranded ? "With Rooster" : "Improved"}</div>
                            <div className="text-xs font-normal text-sky-500">(per hire)</div>
                          </th>
                          <th className="p-3 text-right font-medium text-gray-700">Change</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        <tr className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span>Time to Fill</span>
                              <InfoTooltip 
                                title="Time to Fill Improvement"
                                description="The average number of days from job posting to accepted offer."
                                improvementNote="Automated scheduling reduces coordination delays and manual back-and-forth. Typical improvements range from 25-35% reduction in time to fill."
                                calculationFormula={`Base TTF × (1 - reduction factor) = Improved TTF
where reduction factor = TAS improvement × 0.01
Maximum reduction: 40%`}
                                additionalInfo="This is the most impactful metric for ROI, as every day saved reduces vacancy costs and accelerates productivity gains."
                              />
                            </div>
                          </td>
                          <td className="p-3 text-center">{Math.round(results.metrics?.current?.timeToFill || getNumericValue(formData.timeToFill))} days</td>
                          <td className="p-3 text-center">{Math.round(results.metrics?.improved?.timeToFill || getNumericValue(formData.timeToFill) * 0.75)} days</td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-green-600">-{Math.round(((results.metrics?.current?.timeToFill || getNumericValue(formData.timeToFill)) - (results.metrics?.improved?.timeToFill || getNumericValue(formData.timeToFill) * 0.75)) / (results.metrics?.current?.timeToFill || getNumericValue(formData.timeToFill)) * 100)}%</div>
                            <div className="text-xs text-gray-500">per hire</div>
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span>Cost per Hire</span>
                              <InfoTooltip 
                                title="Cost per Hire"
                                description="The total cost associated with filling a position, including recruitment, administration, and interview expenses."
                                improvementNote="Automation reduces administrative costs through elimination of manual scheduling tasks. Faster hiring processes decrease overhead expenses and recruiter time per position."
                                calculationFormula={`Current cost × (1 - reduction factor) = New cost
where reduction factor = TAS improvement points × 0.0083
Maximum reduction: 30%`}
                                additionalInfo="Cost per hire affects recruiting efficiency and overall talent acquisition budget. Reducing this metric creates direct bottom-line savings."
                              />
                            </div>
                          </td>
                          <td className="p-3 text-center">{formatCurrency(results.metrics?.current?.costPerHire || getNumericValue(formData.costPerHire))}</td>
                          <td className="p-3 text-center">{formatCurrency(results.metrics?.improved?.costPerHire || getNumericValue(formData.costPerHire) * 0.85)}</td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-green-600">-{Math.round(((results.metrics?.current?.costPerHire || getNumericValue(formData.costPerHire)) - (results.metrics?.improved?.costPerHire || getNumericValue(formData.costPerHire) * 0.85)) / (results.metrics?.current?.costPerHire || getNumericValue(formData.costPerHire)) * 100)}%</div>
                            <div className="text-xs text-gray-500">per hire</div>
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span>Vacancy Cost</span>
                              <InfoTooltip 
                                title="Vacancy Cost"
                                description="The cost to your organization of having a position unfilled, calculated as daily vacancy cost × time to fill."
                                improvementNote="Reducing the time-to-fill directly reduces the total vacancy cost per position. Rooster accelerates interview scheduling, which is typically the biggest source of delay in the hiring process."
                                calculationFormula={`Daily Vacancy Cost × Time to Fill
Before: $${getNumericValue(formData.dailyVacancyCost)}/day × ${Math.round(results.metrics?.current?.timeToFill || getNumericValue(formData.timeToFill))} days
After: $${getNumericValue(formData.dailyVacancyCost)}/day × ${Math.round(results.metrics?.improved?.timeToFill || getNumericValue(formData.timeToFill) * 0.75)} days`}
                                additionalInfo="Vacancy costs include lost productivity, missed business opportunities, and additional workload on existing staff. These costs often exceed the position's salary on a per-day basis."
                              />
                            </div>
                          </td>
                          <td className="p-3 text-center">{formatCurrency(results.metrics?.current?.vacancyCost || (getNumericValue(formData.timeToFill) * getNumericValue(formData.dailyVacancyCost)))}</td>
                          <td className="p-3 text-center">{formatCurrency(results.metrics?.improved?.vacancyCost || (getNumericValue(formData.timeToFill) * 0.75 * getNumericValue(formData.dailyVacancyCost)))}</td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-green-600">-{Math.round(((results.metrics?.current?.vacancyCost || (getNumericValue(formData.timeToFill) * getNumericValue(formData.dailyVacancyCost))) - (results.metrics?.improved?.vacancyCost || (getNumericValue(formData.timeToFill) * 0.75 * getNumericValue(formData.dailyVacancyCost)))) / (results.metrics?.current?.vacancyCost || (getNumericValue(formData.timeToFill) * getNumericValue(formData.dailyVacancyCost))) * 100)}%</div>
                            <div className="text-xs text-gray-500">per hire</div>
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span>Interview No-Show Rate</span>
                              <InfoTooltip 
                                title="No-Show Rate Reduction"
                                description="The percentage of scheduled interviews where candidates fail to appear."
                                improvementNote="Automated reminders, better scheduling options with candidate preferences, and improved experience reduce no-shows significantly. Each prevented no-show saves interviewer time and expedites hiring."
                                calculationFormula={`Current rate × (1 - reduction factor) = New rate
where reduction factor = TAS improvement points × 0.014
Maximum reduction: 50%`}
                                additionalInfo="No-shows are costly in terms of wasted interviewer time and delayed hiring decisions. Reducing this metric has a direct impact on hiring efficiency."
                              />
                            </div>
                          </td>
                          <td className="p-3 text-center">{Math.round(getNumericValue(formData.interviewNoShowRate))}%</td>
                          <td className="p-3 text-center">{Math.round(getNumericValue(formData.interviewNoShowRate) * (1 - Math.min(0.60 * (getNumericValue(formData.tasImprovement, 20) / 30), 0.75)))}%</td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-green-600">-{Math.round(Math.min(0.60 * (getNumericValue(formData.tasImprovement, 20) / 30), 0.75) * 100)}%</div>
                            <div className="text-xs text-gray-500">reduction</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    </div>
                </div>
                
                {/* Summary Card - ROI at a glance */}
                <div className="mb-8 bg-gradient-to-br from-gray-50 to-sky-50 p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex flex-col lg:flex-row items-center lg:justify-between">
                    <div className="flex flex-col items-center lg:items-start mb-4 lg:mb-0 text-center lg:text-left">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">ROI Analysis Summary</h2>
                      <p className="text-xs sm:text-sm text-gray-600 max-w-lg">
                        Your expected ROI based on {
                          scenarioMode === 'conservative' ? 'conservative' :
                          scenarioMode === 'optimistic' ? 'optimistic' :
                          'balanced'
                        } assumptions.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 w-full lg:w-auto">
                      <div className="flex flex-col items-center text-center">
                        <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1 flex items-center justify-center">
                          <span className="whitespace-nowrap">Year 1 ROI</span>
                          <InfoTooltip 
                            title="Year 1 ROI"
                            description="ROI accounting for implementation time and adoption ramp"
                            calculationFormula={(() => {
                              const implMonths = Math.ceil(timeToValue.implementationWeeks / 4.33);
                              const totalMonths = implMonths + timeToValue.adoptionRampMonths;
                              if (totalMonths > 12) {
                                return `${implMonths}mo implementation + ${timeToValue.adoptionRampMonths}mo ramp = ${totalMonths}mo total (extends beyond Year 1)`;
                              }
                              return `${implMonths}mo implementation + ${timeToValue.adoptionRampMonths}mo ramp`;
                            })()}
                            additionalInfo={(() => {
                              const totalMonths = Math.ceil(timeToValue.implementationWeeks / 4.33) + timeToValue.adoptionRampMonths;
                              if (totalMonths >= 12) {
                                return "Limited Year 1 benefits due to extended timeline";
                              }
                              const scenarioText = scenarioMode === 'conservative' ? 'Conservative' : 
                                                  scenarioMode === 'optimistic' ? 'Optimistic' : 'Moderate';
                              return `${scenarioText} Year 1 projection with phased adoption`;
                            })()}
                          />
                        </div>
                        <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-600">{results.year1ROI ? results.year1ROI.toFixed(1) : (results.annualRoi || 0).toFixed(1)}x</p>
                        <p className="text-xs text-gray-500 mt-1">{(() => {
                          const totalMonths = Math.ceil(timeToValue.implementationWeeks / 4.33) + timeToValue.adoptionRampMonths;
                          return totalMonths >= 12 ? "Partial year" : "With ramp-up";
                        })()}</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1 flex items-center justify-center">
                          <span className="whitespace-nowrap">Steady-State ROI</span>
                          <InfoTooltip 
                            title="Steady-State ROI"
                            description="Full recurring value after complete adoption"
                            calculationFormula={`Annual Savings / Annual Subscription`}
                            additionalInfo={(() => {
                              const implMonths = Math.ceil(timeToValue.implementationWeeks / 4.33);
                              const totalMonths = implMonths + timeToValue.adoptionRampMonths;
                              if (totalMonths > 12) {
                                return `Full benefits begin in month ${totalMonths + 1}`;
                              }
                              return "Expected ROI from Year 2 onward at full adoption";
                            })()}
                          />
                        </div>
                        <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-600">{results.steadyStateROI ? results.steadyStateROI.toFixed(1) : (results.annualRoi || 0).toFixed(1)}x</p>
                        <p className="text-xs text-gray-500 mt-1">{(() => {
                          const totalMonths = Math.ceil(timeToValue.implementationWeeks / 4.33) + timeToValue.adoptionRampMonths;
                          return totalMonths > 12 ? "At full adoption" : "Years 2+";
                        })()}</p>
                      </div>
                      {results.paybackMetrics?.paybackMonth && (
                        <div className="flex flex-col items-center text-center">
                          <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1 whitespace-nowrap">Payback Period</div>
                          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-sky-600">{results.paybackMetrics.paybackMonth}</p>
                          <p className="text-xs text-gray-500 mt-1">Months</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* ROI Ramp Chart */}
                {results.monthlyProjections && results.monthlyProjections.length > 0 && (
                  <div className="mb-8">
                    <ROIRampChart
                      monthlyProjections={results.monthlyProjections}
                      implementationWeeks={timeToValue.implementationWeeks}
                      adoptionRampMonths={timeToValue.adoptionRampMonths}
                      paybackMonth={results.paybackMetrics?.paybackMonth || 0}
                      scenarioMode={scenarioMode}
                      currency={formData.currency}
                    />
                  </div>
                )}
                
                {/* Your Implementation Plan - Unified Section */}
                <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group">
                  <button
                    type="button"
                    onClick={() => setImplementationPanelOpen(!implementationPanelOpen)}
                    className="w-full px-6 py-5 bg-white hover:bg-gray-50 transition-all duration-300 flex items-center justify-between relative"
                    aria-expanded={implementationPanelOpen}
                    aria-controls="implementation-panel-content"
                  >
                    {/* Left accent bar - dynamic color based on scenario */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                      scenarioMode === 'conservative' ? 'bg-red-500' :
                      scenarioMode === 'optimistic' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`}></div>
                    
                    <div className="flex items-center flex-1">
                      {/* Configuration icon */}
                      <div className={`mr-4 p-2 rounded-lg ${
                        scenarioMode === 'conservative' ? 'bg-red-50' :
                        scenarioMode === 'optimistic' ? 'bg-green-50' :
                        'bg-blue-50'
                      }`}>
                        <svg 
                          className={`w-5 h-5 ${
                            scenarioMode === 'conservative' ? 'text-red-600' :
                            scenarioMode === 'optimistic' ? 'text-green-600' :
                            'text-blue-600'
                          }`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-gray-900">ROI Assumptions</h3>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                scenarioMode === 'conservative' ? 'bg-red-100 text-red-800' :
                                scenarioMode === 'optimistic' ? 'bg-green-100 text-green-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {scenarioMode === 'conservative' ? 'Conservative' : 
                                 scenarioMode === 'optimistic' ? 'Optimistic' : 
                                 'Balanced'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {scenarioMode === 'conservative'
                                ? 'Conservative projections with risk mitigation'
                                : scenarioMode === 'optimistic'
                                ? 'Optimistic projections with accelerated adoption'
                                : 'Industry-standard baseline projections'}
                            </p>
                          </div>
                          {!implementationPanelOpen && (
                            <div className="flex flex-wrap gap-2 ml-0 sm:ml-4 mt-2 sm:mt-0">
                              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {Math.ceil(timeToValue.implementationWeeks / 4.33)}mo timeline
                              </span>
                              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {Math.round(timeToValue.operationalRealizationFactor * 100)}% realization
                              </span>
                              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {investmentApproach} effort
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expand/Collapse indicator */}
                    <div className="ml-4 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200">
                      <svg 
                        className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${implementationPanelOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {/* Collapsible Content */}
                  <div 
                    id="implementation-panel-content"
                    className={`transition-all duration-500 ease-out ${implementationPanelOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
                  >
                    <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100">
                      
                      {/* Section Header */}
                      <p className="text-sm text-gray-600 font-medium mb-4">Analysis Scenario</p>
                      
                      {/* Current Assumptions Summary */}
                      <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Current Calculation Assumptions</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-sky-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 0016 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-600">Implementation:</span>
                                <InfoTooltip
                                  title="Implementation Timeline"
                                  description="Time to fully deploy and configure the scheduling platform across your organization."
                                  improvementNote="Longer timelines delay benefit realization but may reduce implementation risk."
                                  additionalInfo="Affects when you start seeing ROI. Typical implementations range from 2-3 months."
                                />
                              </div>
                              <span className="text-gray-500">{Math.ceil(timeToValue.implementationWeeks / 4.33)} months</span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-sky-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 0016 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-600">Adoption Ramp:</span>
                                <InfoTooltip
                                  title="Adoption Ramp Period"
                                  description="Time for teams to reach full productivity with the new scheduling system."
                                  improvementNote="During ramp-up, benefits are typically reduced to 50% of full potential."
                                  additionalInfo="Realistic adoption curves prevent overestimating Year 1 savings. Most teams reach full adoption in 2-3 months."
                                />
                              </div>
                              <span className="text-gray-500">{timeToValue.adoptionRampMonths} months</span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-sky-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 0016 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-600">Year 1 Realization:</span>
                                <InfoTooltip
                                  title="Operational Realization Factor"
                                  description="Percentage of theoretical benefits actually captured in Year 1."
                                  improvementNote="Accounts for implementation challenges, process adjustments, and organizational readiness."
                                  additionalInfo="Not all benefits are immediate. Conservative: 70%, Moderate: 80%, Optimistic: 90%."
                                />
                              </div>
                              <span className="text-gray-500">{Math.round(timeToValue.operationalRealizationFactor * 100)}%</span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-sky-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 0016 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-600">TTF Attribution:</span>
                                <InfoTooltip
                                  title="Time-to-Fill Attribution"
                                  description="How much of time-to-fill improvement is directly due to better scheduling vs other factors."
                                  improvementNote="Other factors like market conditions, sourcing quality, and compensation also affect time-to-fill."
                                  additionalInfo="Provides a realistic view of scheduling's impact on hiring speed. Typical attribution: 15-30% of TTF improvements."
                                />
                              </div>
                              <span className="text-gray-500">{Math.round(attributionFactors.ttfAttributionFactor * 100)}%</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 italic mt-3 pt-3 border-t">
                          {scenarioMode === 'conservative'
                            ? 'Conservative assumptions ensure achievable projections and account for potential implementation challenges.'
                            : scenarioMode === 'optimistic'
                            ? 'Optimistic projections assume strong support and faster-than-average adoption across the organization.'
                            : 'Balanced assumptions reflect typical enterprise implementations based on industry benchmarks.'}
                        </p>
                      </div>
                      
                      {/* Parameter Controls */}
                      <div className="space-y-6">
                        
                        {/* Analysis Scenario - Primary Global Setting */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Adjust calculation parameters below</label>
                          <p className="text-xs text-gray-500 mb-3">Sets conservative, balanced, or optimistic assumptions for all calculations</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { value: 'conservative', label: 'Conservative', desc: 'Risk-aware projections' },
                              { value: 'moderate', label: 'Balanced', desc: 'Industry benchmarks' },
                              { value: 'optimistic', label: 'Optimistic', desc: 'Growth scenario' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  const newMode = option.value as 'conservative' | 'moderate' | 'optimistic';
                                  
                                  // Determine what to apply based on user actions
                                  const shouldApplyImplementation = !showAdvancedSettings && !timelineManuallyChanged;
                                  const shouldApplyEffort = !showAdvancedSettings && !effortManuallyChanged;
                                  
                                  // Always apply scenario preset (adoption ramp, realization factors, etc.)
                                  // But only include implementation/effort if conditions are met
                                  const newInvestmentApproach = applyScenarioPreset(newMode, shouldApplyImplementation);
                                  
                                  // Update investment approach if it should be applied
                                  if (shouldApplyEffort && newInvestmentApproach) {
                                    setInvestmentApproach(newInvestmentApproach);
                                  }
                                  
                                  calculateCompleteROI({ scenarioMode: newMode });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                className={`p-4 rounded-lg border-2 transition-all ${
                                  scenarioMode === option.value
                                    ? 'border-sky-500 bg-sky-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                                }`}
                              >
                                <div className="font-medium text-sm">{option.label}</div>
                                <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Implementation Timeline */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Implementation Timeline</label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { value: 'standard', weeks: 6, label: 'Standard', desc: '1-2 months' },
                              { value: 'adaptive', weeks: 14, label: 'Adaptive', desc: '3-4 months' },
                              { value: 'tailored', weeks: 18, label: 'Tailored', desc: '4-5 months' }
                            ].map((option, index) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  // Mark timeline as manually changed
                                  setTimelineManuallyChanged(true);
                                  
                                  // Only update implementation weeks, not adoption ramp
                                  const newTimeToValue: TimeToValueInputs = {
                                    ...timeToValue,
                                    implementationWeeks: option.weeks
                                  };
                                  updateTimeToValue({ 
                                    implementationWeeks: option.weeks
                                  });
                                  // Pass explicit timeToValue to avoid state batching issues
                                  calculateCompleteROI({ timeToValue: newTimeToValue });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                className={`p-4 rounded-lg border-2 transition-all ${
                                  (option.value === 'standard' && timeToValue.implementationWeeks <= 8) ||
                                  (option.value === 'adaptive' && timeToValue.implementationWeeks > 8 && timeToValue.implementationWeeks < 17) ||
                                  (option.value === 'tailored' && timeToValue.implementationWeeks >= 17)
                                    ? 'border-sky-500 bg-sky-50 shadow-md' 
                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="font-medium text-sm text-gray-800">{option.label}</div>
                                <div className="text-xs mt-1 text-gray-500">{option.desc}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Implementation Effort */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Implementation Effort</label>
                          <p className="text-xs text-gray-500 mb-3">Internal resources needed for rollout and change management</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { value: 'minimal', label: 'Minimal', desc: 'Light touch - minimal internal resources' },
                              { value: 'standard', label: 'Standard', desc: 'Balanced - typical internal effort' },
                              { value: 'comprehensive', label: 'Comprehensive', desc: 'Full rollout - extensive internal support' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  // Mark effort as manually changed
                                  setEffortManuallyChanged(true);
                                  
                                  // Update the investment approach state
                                  setInvestmentApproach(option.value as 'minimal' | 'standard' | 'comprehensive');
                                  
                                  // Calculate the base implementation cost (Standard level)
                                  const subscription = getAnnualSubscriptionCost(getNumericValue(formData.annualHires));
                                  const baseInternalCost = 20 * timeToValue.implementationWeeks * 150; // 20 hrs/week × weeks × $150/hr
                                  const vendorSupportCost = subscription * 0.1;
                                  const baseTotalCost = baseInternalCost + vendorSupportCost;
                                  
                                  // Apply multiplier based on selection
                                  let newInvestment: ImplementationInvestment;
                                  if (option.value === 'minimal') {
                                    // Minimal: 50% of standard effort
                                    newInvestment = { mode: 'custom', customAmount: Math.round(baseTotalCost * 0.5) };
                                    updateImplementationInvestment(newInvestment);
                                  } else if (option.value === 'standard') {
                                    // Standard: baseline (100%)
                                    newInvestment = { mode: 'auto' }; // This will calculate the standard amount
                                    updateImplementationInvestment(newInvestment);
                                  } else {
                                    // Comprehensive: 150% of standard effort
                                    newInvestment = { mode: 'custom', customAmount: Math.round(baseTotalCost * 1.5) };
                                    updateImplementationInvestment(newInvestment);
                                  }
                                  
                                  // Pass explicit implementation investment to avoid state batching issues
                                  calculateCompleteROI({ implementationInvestment: newInvestment });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                className={`p-4 rounded-lg border-2 transition-all ${
                                  investmentApproach === option.value
                                    ? 'border-sky-500 bg-sky-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                                }`}
                              >
                                <div className="font-medium text-sm">{option.label}</div>
                                <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Advanced Settings Toggle - Only for Admin */}
                        {hasAdvancedSettings && (
                          <div className="pt-4 border-t border-gray-200">
                            <button
                            type="button"
                            onClick={() => {
                              const newShowAdvanced = !showAdvancedSettings;
                              setShowAdvancedSettings(newShowAdvanced);
                              
                              // If hiding advanced settings, reset manual flags and reapply scenario presets
                              if (!newShowAdvanced) {
                                setTimelineManuallyChanged(false);
                                setEffortManuallyChanged(false);
                                
                                // Reapply current scenario's timeline and effort presets
                                const newInvestmentApproach = applyScenarioPreset(scenarioMode, true);
                                if (newInvestmentApproach) {
                                  setInvestmentApproach(newInvestmentApproach);
                                }
                                calculateCompleteROI();
                              }
                            }}
                            className="flex items-center text-sm text-sky-600 hover:text-sky-700 font-medium"
                          >
                            <svg 
                              className={`w-4 h-4 mr-2 transform transition-transform ${showAdvancedSettings ? 'rotate-90' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            {showAdvancedSettings ? 'Hide' : 'Show'} Advanced Settings
                          </button>
                          </div>
                        )}

                        {/* Advanced Settings (Hidden by default) */}
                        {hasAdvancedSettings && showAdvancedSettings && (
                          <div className="pt-4 space-y-6 overflow-visible">
                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                              <p className="text-sm text-yellow-800">
                                <strong>Advanced users only:</strong> These settings allow fine-tuning of implementation assumptions. 
                                Most users should use the simple choices above.
                              </p>
                            </div>

                            {/* Investment Mode Component */}
                            <div className="mb-6">
                              <InvestmentMode
                                mode={implementationInvestment.mode}
                                customAmount={implementationInvestment.customAmount}
                                sponsorHours={implementationInvestment.sponsorHours}
                                opsHours={implementationInvestment.opsHours}
                                itHours={implementationInvestment.itHours}
                                trainingHours={implementationInvestment.trainingHours}
                                miscHours={implementationInvestment.miscHours}
                                hourlyRates={implementationInvestment.hourlyRates}
                                onModeChange={(mode) => {
                                  const newInvestment = updateImplementationInvestment({ mode });
                                  calculateCompleteROI({ implementationInvestment: newInvestment });
                                }}
                                onCustomAmountChange={(amount) => {
                                  const newInvestment = updateImplementationInvestment({ customAmount: amount });
                                  calculateCompleteROI({ implementationInvestment: newInvestment });
                                }}
                                onHoursChange={(role, hours) => {
                                  const update: any = {};
                                  update[`${role}Hours`] = hours;
                                  const newInvestment = updateImplementationInvestment(update);
                                  calculateCompleteROI({ implementationInvestment: newInvestment });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                onRateChange={(role, rate) => {
                                  const currentRates = implementationInvestment.hourlyRates || {
                                    sponsor: 150,
                                    ops: 100,
                                    it: 125,
                                    training: 75,
                                    misc: 100,
                                  };
                                  const newInvestment = updateImplementationInvestment({
                                    hourlyRates: { ...currentRates, [role]: rate }
                                  });
                                  calculateCompleteROI({ implementationInvestment: newInvestment });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                currency={formData.currency as 'USD' | 'EUR' | undefined}
                              />
                            </div>

                            {/* Timeline Settings - Full Width */}
                            <div className="mb-6">
                              <TimelineSettings
                                implementationWeeks={timeToValue.implementationWeeks}
                                adoptionRampMonths={timeToValue.adoptionRampMonths}
                                earlyWinsEnabled={timeToValue.earlyWinsEnabled}
                                onImplementationWeeksChange={(weeks) => {
                                  const newTimeToValue = updateTimeToValue({ implementationWeeks: weeks });
                                  calculateCompleteROI({ timeToValue: newTimeToValue });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                onAdoptionRampMonthsChange={(months) => {
                                  const newTimeToValue = updateTimeToValue({ adoptionRampMonths: months });
                                  calculateCompleteROI({ timeToValue: newTimeToValue });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                onEarlyWinsToggle={(enabled) => {
                                  const newTimeToValue = updateTimeToValue({ earlyWinsEnabled: enabled });
                                  calculateCompleteROI({ timeToValue: newTimeToValue });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                isExpanded={timelineSettingsExpanded}
                                onToggleExpand={() => setTimelineSettingsExpanded(!timelineSettingsExpanded)}
                              />
                            </div>

                            {/* Realization Factors - Full Width */}
                            <div className="mb-6">
                              <RealizationFactors
                                operationalRealizationFactor={timeToValue.operationalRealizationFactor}
                                ttfAttributionFactor={attributionFactors.ttfAttributionFactor}
                                roleVariabilityDiscount={attributionFactors.roleVariabilityDiscount}
                                onOperationalRealizationChange={(value) => {
                                  const newTimeToValue = updateTimeToValue({ operationalRealizationFactor: value });
                                  calculateCompleteROI({ timeToValue: newTimeToValue });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                onTTFAttributionChange={(value) => {
                                  const newAttributionFactors = updateAttributionFactors({ ttfAttributionFactor: value });
                                  calculateCompleteROI({ attributionFactors: newAttributionFactors });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                onRoleVariabilityChange={(value) => {
                                  const newAttributionFactors = updateAttributionFactors({ roleVariabilityDiscount: value });
                                  calculateCompleteROI({ attributionFactors: newAttributionFactors });
                                  setReportFinalized(false);
                                  clearPDFAndReportWindow();  // Clear old PDF and report window
                                }}
                                isExpanded={realizationFactorsExpanded}
                                onToggleExpand={() => setRealizationFactorsExpanded(!realizationFactorsExpanded)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Savings Analysis Section */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex justify-between items-center mb-4 border-b pb-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-800">
                      <span className="text-sky-600 mr-2">💰</span>Savings Analysis
                    </h3>
                    <div className="text-xs text-sky-500 italic">Year 1 vs Steady-State</div>
                  </div>
                  
                  <p className="text-xs sm:text-sm text-gray-600 mb-4">
                    These calculations show the realistic financial impact of implementing Rooster. Year 1 accounts for implementation 
                    and adoption timeline, while Steady-State represents the full value from Year 2 onward.
                  </p>
                  
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="overflow-hidden rounded-lg shadow min-w-[500px]">
                    <table className="w-full text-xs sm:text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="p-3 text-left font-medium text-gray-700">Savings Category</th>
                          <th className="p-3 text-right font-medium text-gray-700">
                            Year 1<sup className="text-xs">*</sup>
                          </th>
                          <th className="p-3 text-right font-medium text-gray-700">
                            Steady-State<sup className="text-xs">**</sup>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        <tr className="relative">
                          <td className="p-3 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <span>Vacancy Cost Reduction</span>
                              <InfoTooltip 
                                title="Vacancy Cost Reduction"
                                description="The financial benefit of reducing unfilled position durations."
                                calculationFormula={`(${Math.round(results.metrics?.current?.timeToFill || 0)} days - ${Math.round(results.metrics?.improved?.timeToFill || 0)} days) × $${Math.round(getNumericValue(formData.dailyVacancyCost))}/day × ${getNumericValue(formData.annualHires)} hires`}
                                additionalInfo="Year 1 includes time-to-value adjustments and attribution factors."
                              />
                            </div>
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium">
                            {formatCurrency(results.enhancedSavings?.year1Savings?.timeToFillSavings || 0)}
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium text-green-600">
                            {formatCurrency(results.enhancedSavings?.steadyStateSavings?.timeToFillSavings || results.savings?.timeToFillSavings || 0)}
                          </td>
                        </tr>
                        <tr className="relative">
                          <td className="p-3 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <span>Hiring Cost Reduction</span>
                              <InfoTooltip 
                                title="Hiring Cost Reduction"
                                description="Savings from reducing per-hire recruitment and administrative costs."
                                calculationFormula={`(${formatCurrency(results.metrics?.current?.costPerHire || 0)} - ${formatCurrency(results.metrics?.improved?.costPerHire || 0)}) × ${getNumericValue(formData.annualHires)} hires`}
                                additionalInfo="Year 1 reflects phased adoption during implementation."
                              />
                            </div>
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium">
                            {formatCurrency(results.enhancedSavings?.year1Savings?.costPerHireSavings || 0)}
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium text-green-600">
                            {formatCurrency(results.enhancedSavings?.steadyStateSavings?.costPerHireSavings || results.savings?.costPerHireSavings || 0)}
                          </td>
                        </tr>
                        <tr className="relative">
                          <td className="p-3 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <span>Productivity Gains</span>
                              <InfoTooltip 
                                title="Productivity Gains"
                                description="Value of additional days positions are filled and productive."
                                calculationFormula={`(${Math.round(results.metrics?.current?.timeToFill || 0)} days - ${Math.round(results.metrics?.improved?.timeToFill || 0)} days) × ($${Math.round(getNumericValue(formData.avgSalary))}/250 days × 10%) × ${getNumericValue(formData.annualHires)} hires`}
                                additionalInfo="Based on 10% of daily salary value as productivity contribution."
                              />
                            </div>
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium">
                            {formatCurrency(results.enhancedSavings?.year1Savings?.productivityGain || 0)}
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium text-green-600">
                            {formatCurrency(results.enhancedSavings?.steadyStateSavings?.productivityGain || results.savings?.productivityGain || 0)}
                          </td>
                        </tr>
                        <tr className="relative">
                          <td className="p-3 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <span>Admin Time Savings</span>
                              <InfoTooltip 
                                title="Admin Time Savings"
                                description="Value of recruiter time saved through scheduling automation."
                                calculationFormula={`${Math.round(Math.min(0.70 * (getNumericValue(formData.tasImprovement, 20) / 30), 0.80) * 100)}% reduction × 3 hours/hire × $31.25/hour × ${getNumericValue(formData.annualHires)} hires`}
                                additionalInfo="Based on average recruiter salary of $65,000. Year 1 includes operational realization factor."
                              />
                            </div>
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium">
                            {formatCurrency(results.enhancedSavings?.year1Savings?.schedulingTimeSavings || 0)}
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium text-green-600">
                            {formatCurrency(results.enhancedSavings?.steadyStateSavings?.schedulingTimeSavings || results.savings?.schedulingTimeSavings || 0)}
                          </td>
                        </tr>
                        <tr className="relative">
                          <td className="p-3 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <span>No-Show Reduction</span>
                              <InfoTooltip 
                                title="No-Show Reduction Savings"
                                description="Value of interviewer time saved by reducing candidate no-shows."
                                calculationFormula={`${Math.round(getNumericValue(formData.interviewNoShowRate))}% → ${Math.round(getNumericValue(formData.interviewNoShowRate) * (1 - Math.min(0.60 * (getNumericValue(formData.tasImprovement, 20) / 30), 0.75)))}% no-show rate`}
                                additionalInfo="Based on 4 interviews per hire and $84/hour fully-loaded interviewer cost."
                              />
                            </div>
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium">
                            {formatCurrency(results.enhancedSavings?.year1Savings?.noShowReductionSavings || 0)}
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-medium text-green-600">
                            {formatCurrency(results.enhancedSavings?.steadyStateSavings?.noShowReductionSavings || results.savings?.noShowReductionSavings || 0)}
                          </td>
                        </tr>
                        <tr className="bg-gradient-to-r from-sky-50 to-cyan-50">
                          <td className="p-3 border-t border-gray-200 font-bold">Total Annual Savings</td>
                          <td className="p-3 border-t border-gray-200 text-right font-bold text-orange-600">
                            {formatCurrency(results.enhancedSavings?.year1Savings?.totalAnnualSavings || 0)}
                          </td>
                          <td className="p-3 border-t border-gray-200 text-right font-bold text-green-600">
                            {formatCurrency(results.enhancedSavings?.steadyStateSavings?.totalAnnualSavings || results.savings?.totalAnnualSavings || 0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <span className="font-medium">* Year 1:</span> Includes {Math.ceil(timeToValue.implementationWeeks / 4.33)}-month implementation, 
                          {timeToValue.adoptionRampMonths}-month adoption ramp at 50% benefit, 
                          and {Math.round(timeToValue.operationalRealizationFactor * 100)}% operational realization
                        </div>
                        <div className="flex-1">
                          <span className="font-medium">** Steady-State:</span> Full value from Year 2 onward at 100% adoption
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
                
                {/* Dynamic ROI Drivers Section */}
                <div className="mb-8 mt-8">
                  <div className="flex justify-between items-center mb-4 border-b pb-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center">
                      <img src={roosterIcon} alt="Rooster" className="w-6 h-6 mr-2" />
                      Your Top ROI Drivers
                    </h3>
                    <div className="text-xs text-gray-500">
                      Based on your TAS component scores
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-6">
                    These features are specifically recommended for your organization based on your current TAS scores. 
                    Each addresses your weakest components and contributes directly to your projected ${formatCurrency(results.enhancedSavings?.steadyStateSavings?.totalAnnualSavings || results.savings?.totalAnnualSavings || 0).replace('$', '')} in annual savings.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(() => {
                      // Get recommended features based on weak components
                      const recommendedFeatures = getRecommendedFeatures(results.tasComponents, 8);
                      
                      // Calculate ROI contribution for each feature
                      const featuresWithROI = recommendedFeatures.map(feature => {
                        const totalImpact = calculateFeatureTotalImpact(feature);
                        // Estimate ROI contribution as percentage of total savings
                        const roiContribution = (totalImpact / 100) * 0.15; // Each point contributes roughly 15% of savings
                        const estimatedSavings = (results.savings?.totalAnnualSavings || 0) * roiContribution;
                        
                        return {
                          ...feature,
                          estimatedSavings,
                          roiPercentage: Math.round(roiContribution * 100)
                        };
                      });
                      
                      return featuresWithROI.slice(0, 8).map(feature => (
                        <div 
                          key={feature.id}
                          className="flex flex-col p-4 bg-white rounded-lg border border-gray-100 hover:border-sky-200 hover:bg-sky-50 transition-all duration-200 shadow-sm hover:shadow group h-full"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="text-2xl">{feature.icon || '🚀'}</div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-sky-600">
                                {formatCurrency(feature.estimatedSavings)}
                              </div>
                              <div className="text-xs text-gray-500">annual savings</div>
                            </div>
                          </div>
                          <h4 className="font-medium text-gray-800 mb-2">{feature.name}</h4>
                          <p className="text-xs text-gray-600 mb-3 flex-grow">{feature.whyItMatters}</p>
                          <div className="pt-2 border-t border-gray-100">
                            <div className="text-center">
                              <span className="text-xs text-gray-500">
                                {feature.roiPercentage}% of total ROI
                              </span>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>


                {/* Report Finalization Section - Two-State Design */}
                <div className={`transition-all duration-500 ease-in-out ${reportFinalized ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-r from-sky-50 to-cyan-50 border-sky-100'} p-8 rounded-lg mb-8 border shadow-sm`}>
                  {!reportFinalized ? (
                    // State 1: Pre-Finalization
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-800 mb-3">Complete Your ROI Analysis</h3>
                      <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                        Review your results above. You can adjust any parameters to see how different scenarios 
                        affect your ROI. When you're satisfied with your analysis, finalize your report.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => goToStep(CalculatorStep.TasScore)}
                          className="px-4 sm:px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 transition-all duration-200 text-sm sm:text-base min-h-[44px]"
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          onClick={finalizeReport}
                          disabled={sessionSaving}
                          className="px-10 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold rounded-md hover:from-red-700 hover:to-red-600 shadow-lg transition-all duration-200 flex items-center justify-center"
                        >
                          {sessionSaving ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Finalizing...
                            </>
                          ) : (
                            <>Finalize Report →</>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // State 2: Post-Finalization
                    <div className="text-center">
                      <div className="flex justify-center mb-3">
                        <div className="bg-green-100 rounded-full p-3">
                          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">Your Report is Ready</h3>
                      
                      {/* Report Link Display - Enhanced */}
                      {currentReportId && (
                        <div className="mb-6 p-5 bg-gradient-to-br from-blue-50 to-sky-50 border border-sky-200 rounded-lg max-w-xl mx-auto shadow-sm">
                          <div className="flex items-start gap-2 mb-3">
                            {/* Bookmark Icon */}
                            <svg className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-base font-medium text-gray-700">Your permanent report link</p>
                              <p className="text-sm text-gray-500 mt-0.5">Bookmark or save it - this link never expires</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between bg-white p-3 rounded border border-gray-200">
                            <code className="text-sm text-gray-700 truncate flex-1 font-mono">
                              {window.location.origin}/report/{currentReportId}
                            </code>
                            <button
                              type="button"
                              onClick={() => {
                                const url = `${window.location.origin}/report/${currentReportId}`;
                                navigator.clipboard.writeText(url).then(() => {
                                  // Show temporary success message with checkmark
                                  const btn = document.getElementById('copy-btn');
                                  if (btn) {
                                    const originalHTML = btn.innerHTML;
                                    btn.innerHTML = `
                                      <svg class="w-4 h-4 mr-1.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                      </svg>
                                      Copied!
                                    `;
                                    btn.classList.add('bg-green-600', 'hover:bg-green-700');
                                    btn.classList.remove('bg-sky-600', 'hover:bg-sky-700');
                                    setTimeout(() => {
                                      btn.innerHTML = originalHTML;
                                      btn.classList.remove('bg-green-600', 'hover:bg-green-700');
                                      btn.classList.add('bg-sky-600', 'hover:bg-sky-700');
                                    }, 2000);
                                  }
                                }).catch(() => {
                                  alert('Failed to copy. Please select and copy the URL manually.');
                                });
                              }}
                              id="copy-btn"
                              className="ml-2 px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded hover:bg-sky-700 transition-all duration-200 flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy Link
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Report Actions */}
                      <div className="mb-6 flex flex-col sm:flex-row justify-center gap-3">
                        <button
                          type="button"
                          onClick={enterEditMode}
                          className="w-full sm:w-auto px-6 sm:px-8 py-3 text-gray-700 font-medium rounded-md shadow-md transition-all duration-200 flex items-center justify-center bg-gray-100 hover:bg-gray-200 border border-gray-300"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit Report
                        </button>
                        
                        <button
                          type="button"
                          onClick={downloadPDFReport}
                          disabled={isGeneratingReport}
                          className={`w-full sm:w-auto px-6 sm:px-8 py-3 text-white font-medium rounded-md shadow-md transition-all duration-200 flex items-center justify-center ${
                            isGeneratingReport 
                              ? 'bg-sky-500 cursor-not-allowed opacity-80' 
                              : 'bg-gradient-to-r from-sky-600 to-cyan-500 hover:from-sky-700 hover:to-cyan-600'
                          }`}
                        >
                          {isGeneratingReport ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Generating Report...
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Report
                            </>
                          )}
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => {
                            if (currentReportId) {
                              setEmailDialogOpen(true);
                            }
                          }}
                          disabled={!currentReportId}
                          className="w-full sm:w-auto px-6 sm:px-8 py-3 text-white font-medium rounded-md shadow-md transition-all duration-200 flex items-center justify-center bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Email Report
                        </button>
                      </div>
                      
                      {/* Divider */}
                      <div className="my-6 border-t border-gray-200"></div>
                      
                      {/* Call to Action */}
                      <div className="mt-6">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Ready to achieve these results?</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Schedule a personalized demo to see how Rooster can transform your interview scheduling process.
                        </p>
                        <a
                          href="https://rooster.us/demo"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-10 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-md hover:from-green-700 hover:to-green-600 shadow-lg transition-all duration-200"
                        >
                          Schedule Demo with Rooster →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Fine print disclaimer */}
                <div className="text-xs text-gray-500 italic text-center mb-4">
                  All calculations are based on industry benchmarks and actual customer data. 
                  Your specific results may vary based on your organization's size, industry, and implementation.
                </div>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
      
      {/* Email Dialog */}
      <EmailDialog
        isOpen={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false);
          setReportHTMLContent(null); // Clear HTML content when dialog closes
        }}
        sessionId={sessionId}
        defaultRecipient={formData.contactEmail}
        defaultRecipientName={formData.contactName}
        organizationName={formData.organizationName}
        senderName={formData.contactName}
        userEmail={formData.contactEmail}  // Pass the user's email for locating session data
        sessionSaved={sessionSaved}  // Pass session saved state
        reportHTMLContent={reportHTMLContent}  // Pass the HTML content from the report tab
        reportUrl={currentReportId ? `${window.location.origin}/${hasROIAnalysis ? 'report' : 'tas-report'}/${currentReportId}` : undefined}  // Pass the report URL
        // Always pass current data for consistency
        formData={formData}
        results={results}
        enhancedData={{
          scenarioMode: scenarioMode,
          annualSubscription: getAnnualSubscriptionCost(getNumericValue(formData.annualHires)),
          implementationInvestment: implementationInvestment.mode === 'custom' 
            ? implementationInvestment.customAmount 
            : calculateImplementationInvestment(implementationInvestment, getAnnualSubscriptionCost(getNumericValue(formData.annualHires)), timeToValue.implementationWeeks),
          paybackPeriod: results?.paybackMetrics?.paybackMonth || 3,
          year1ROI: results?.year1ROI || (results?.annualRoi ? results.annualRoi * 0.75 : 2.5),
          steadyStateROI: results?.steadyStateROI || results?.annualRoi || 3.5,
          enhancedSavings: results?.enhancedSavings || {
            steadyStateSavings: results?.savings
          },
          topPerformerTAS: calculateTopPerformerTAS(formData.industry),
          industryAverageTAS: calculateIndustryTAS(formData.industry),
          componentData: getComponentData(),
          improvementData: getImprovementData(),
          tasComponents: results?.tasComponents
        }}
      />

      {/* Hiring Profile Calculator Modal */}
      <HiringProfileCalculator
        isOpen={showHiringCalculator}
        onClose={() => setShowHiringCalculator(false)}
        onApply={(values) => {
          // Apply both salary and vacancy cost
          handleInputChange({ target: { name: 'avgSalary', value: String(values.salary) } } as any);
          handleInputChange({ target: { name: 'dailyVacancyCost', value: String(values.vacancy) } } as any);
          setCalculationBasis(values.basis);
          setShowHiringCalculator(false);
        }}
        industryName={formData.industry}
        currentSalary={formData.avgSalary}
        currentVacancy={formData.dailyVacancyCost}
      />

      {/* Field Help Modal */}
      <FieldHelpModal
        isOpen={!!helpModalField}
        onClose={() => setHelpModalField(null)}
        content={helpModalField && FIELD_HELP_CONTENT[helpModalField] ? FIELD_HELP_CONTENT[helpModalField](formData.industry as IndustryName) : null}
        industryName={formData.industry}
      />
    </div>
  );
};

export default SchedulingCalculator;